import { Table, TableHeader, TableBody, Pagination } from '../../../components/table';
import Search from '../../../components/table/Search';
import ActionIconButton from '../../../components/ui/ActionIconButton';

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Icon } from '@iconify/react';
import NoData from '../../../components/common/NoData';
import { Dialog } from '../../../components/ui';
import { toast } from '../../../components/ui/notify';
import { friendlyMessage } from '../../../components/ui/friendlyMessage';
import 'react-toastify/dist/ReactToastify.css';

import {
  getPasswordRequirementState,
  validateDateInput,
  validatePassword as validateSharedPassword
} from '../../../utils/validation';
import useDebounce from '../../../hooks/useDebounce';
import { usePermissions } from '../../../context/PermissionsContext';
import {
  getTraineeDetails,
  activeInactiveTrainee,
  fetchUserDetails,
  deleteTrainee,
  editTraineeDetails,
  createNewOrganisationUser,
  bulkValidateUsersJSON,
  bulkUploadUsersJSON,
  getTraineesAnalytics,
} from '../../../services/organization/trainees/trainees.service';
import {
  addJsonSheet,
  createWorkbook,
  downloadWorkbook,
  readSpreadsheetRowsFromFile,
} from '../../../utils/excelWorkbook';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { encodeData } from '../../../utils/encodeAndEncode';
import { apiDateOnlyToDateInputValue } from '../../../utils/dateOnly';
import {
  getCountryByCode,
  getDefaultPhoneCountryCode,
  getPhoneRuleFromCountry,
  getBulkNationalPhoneValidationMessage,
  validateNationalPhoneDigits,
  phoneLengthPlaceholder,
  phoneLengthHintSuffix,
} from '../../../utils/phoneCountryRules';
import CountryCodeDropdown from '../../../components/common/CountryCodeDropdown';
import SingleDatePicker from '../../../components/common/SingleDatePicker';
import { useTimezone } from '../../../context/TimezoneContext';
import { formatUtcInTimeZone } from '../../../utils/timezone';
const DEFAULT_TRAINEE_PHONE_CODE = getDefaultPhoneCountryCode();

/**
 * GET /admin/trainees/get_trainee_details/:id → ApiController returns { success, data }.
 * Canonical `data` (flat):
 *   id, profile_pic_url, name, mobile, mobileno_code, email, is_blocked,
 *   createdAt, updatedAt, role_name, role_id, certificate_count
 * Optional: date_of_birth, date_joined, gender, employee fields, bio, address, etc.
 * profile_pic_url = display (often presigned); profile_pic_storage_key = S3 key for saves.
 * Legacy: personal_information, employee_details, profile_pic.
 */
function mapTraineeApiDetailToFormFields(d) {
  if (!d || typeof d !== 'object') return null;

  const getStringValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'string') return value;
    if (value && typeof value === 'object') {
      if (value.type === 'Buffer' && Array.isArray(value.data)) {
        try {
          return String.fromCharCode.apply(null, value.data);
        } catch {
          return '';
        }
      }
      if (value instanceof Uint8Array || (value.constructor && value.constructor.name === 'Uint8Array')) {
        try {
          return String.fromCharCode.apply(null, Array.from(value));
        } catch {
          return '';
        }
      }
      if (value.data !== undefined) return getStringValue(value.data);
      const stringValue = value.toString();
      if (stringValue === '[object Object]') return '';
      return stringValue;
    }
    return String(value);
  };

  const normalizeGender = (g) => {
    const s = getStringValue(g).toLowerCase();
    return ['male', 'female', 'other'].includes(s) ? s : '';
  };

  const hasNestedPersonal =
    d.personal_information &&
    typeof d.personal_information === 'object' &&
    Object.keys(d.personal_information).length > 0;
  const hasNestedEmployee =
    d.employee_details &&
    typeof d.employee_details === 'object' &&
    Object.keys(d.employee_details).length > 0;

  const personalInfo = hasNestedPersonal
    ? d.personal_information
    : {
        name: d.name,
        email: d.email,
        mobile: d.mobile,
        mobilenocode: d.mobileno_code,
        mobileno_code: d.mobileno_code,
        date_of_birth: d.date_of_birth,
        date_joined: d.date_joined,
        gender: d.gender,
        employee_no: d.employee_no,
        company_code: d.company_code,
        company_description: d.company_description,
        ic_no: d.ic_no,
        fin_no: d.fin_no,
        pwm_rank: d.pwm_rank,
        job_title: d.job_title,
      };

  const employeeDetails = hasNestedEmployee
    ? d.employee_details
    : {
        employee_no: d.employee_no,
        company_code: d.company_code,
        company_description: d.company_description,
        ic_no: d.ic_no,
        fin_no: d.fin_no,
        pwm_rank: d.pwm_rank,
        job_title: d.job_title,
        gender: d.gender,
      };

  const previewUrl = getStringValue(d.profile_pic_url ?? d.profile_pic ?? '');
  const storageKey = getStringValue(d.profile_pic_storage_key ?? '');
  const picForForm =
    storageKey ||
    (previewUrl && !/^https?:\/\//i.test(previewUrl) ? previewUrl : '');

  const dobRaw = personalInfo.date_of_birth ?? d.date_of_birth;
  const joinedRaw = personalInfo.date_joined ?? d.date_joined;
  const countryFromPersonal =
    personalInfo.mobileno_code || personalInfo.mobilenocode || d.mobileno_code;

  return {
    profileImage: null,
    profile_pic_url: picForForm,
    previewUrl,
    upload_image_url: null,
    name: getStringValue(personalInfo.name),
    email: getStringValue(personalInfo.email),
    phoneNumber: getStringValue(personalInfo.mobile),
    countryCode: getStringValue(countryFromPersonal) || DEFAULT_TRAINEE_PHONE_CODE,
    employeeNo: getStringValue(employeeDetails.employee_no || personalInfo.employee_no),
    companyCode: getStringValue(employeeDetails.company_code || personalInfo.company_code),
    companyDescription: getStringValue(employeeDetails.company_description || personalInfo.company_description),
    icNo: getStringValue(employeeDetails.ic_no || personalInfo.ic_no),
    finNo: getStringValue(employeeDetails.fin_no || personalInfo.fin_no),
    gender: normalizeGender(employeeDetails.gender || personalInfo.gender),
    dateOfBirth: apiDateOnlyToDateInputValue(dobRaw),
    dateJoined: apiDateOnlyToDateInputValue(joinedRaw),
    pwmRank: getStringValue(employeeDetails.pwm_rank || personalInfo.pwm_rank),
    jobTitle: getStringValue(employeeDetails.job_title || personalInfo.job_title),
  };
}

export const BULK_TRAINEE_COLUMNS = [
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'gender', label: 'Gender' },
  { key: 'date_of_birth', label: 'Date of Birth' },
  { key: 'password', label: 'Password' },
  { key: 'mobileno_code', label: 'Country Code' },
  { key: 'mobile', label: 'Phone Number' },
  { key: 'employee_no', label: 'Employee No' },
  { key: 'company_code', label: 'Company Code' },
  { key: 'company_description', label: 'Company Description' },
  { key: 'job_title', label: 'Job Title' },
  { key: 'date_joined', label: 'Date Joined' },
  { key: 'ic_no', label: 'IC No' },
  { key: 'fin_no', label: 'FIN No' },
  { key: 'pwm_rank', label: 'PWM Rank' }
];

const BULK_TRAINEE_COLUMN_LOOKUP = new Map(
  BULK_TRAINEE_COLUMNS.map((column) => [column.key, column.label])
);

const BULK_TRAINEE_EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const BULK_TRAINEE_VALID_GENDERS = ['Male', 'Female', 'Other'];

const BULK_TRAINEE_FIELD_ALIASES = {
  name: ['Name', 'name'],
  email: ['Email', 'email'],
  gender: ['Gender', 'gender'],
  date_of_birth: ['Date of Birth', 'date_of_birth', 'DateOfBirth'],
  password: ['Password', 'password'],
  mobileno_code: ['Country Code', 'mobileno_code', 'CountryCode'],
  mobile: ['Phone Number', 'mobile', 'PhoneNumber'],
  employee_no: ['Employee No', 'employee_no', 'EmployeeNo'],
  company_code: ['Company Code', 'company_code', 'CompanyCode'],
  company_description: ['Company Description', 'company_description', 'CompanyDescription'],
  job_title: ['Job Title', 'job_title', 'JobTitle'],
  date_joined: ['Date Joined', 'date_joined', 'DateJoined'],
  ic_no: ['IC No', 'ic_no', 'ICNo'],
  fin_no: ['FIN No', 'fin_no', 'FINNo'],
  pwm_rank: ['PWM Rank', 'pwm_rank', 'PWMRank']
};

const BULK_TRAINEE_SERIAL_ROW_OFFSET = 1;

const createBulkIssueId = (rowNumber, field, message) => `${rowNumber}:${field || 'row'}:${message}`;

const createBulkIssueMap = (issues = []) => {
  return issues.reduce((acc, issue) => {
    if (!acc[issue.field]) {
      acc[issue.field] = [];
    }
    acc[issue.field].push(issue.message);
    return acc;
  }, {});
};

const toBulkString = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  if (typeof value === 'string') {
    return value.trim();
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return String(value).trim();
  }

  if (value instanceof Date && !Number.isNaN(value.valueOf())) {
    const day = String(value.getDate()).padStart(2, '0');
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const year = value.getFullYear();
    return `${day}-${month}-${year}`;
  }

  if (value && typeof value === 'object') {
    if (value.type === 'Buffer' && Array.isArray(value.data)) {
      try {
        return String.fromCharCode.apply(null, value.data).trim();
      } catch (error) {
        return '';
      }
    }

    if (value.data !== undefined) {
      return toBulkString(value.data);
    }
  }

  return String(value).trim();
};

const formatBulkDateParts = (day, month, year) => {
  const normalizedDay = String(day).padStart(2, '0');
  const normalizedMonth = String(month).padStart(2, '0');
  const normalizedYear = String(year).padStart(4, '0');

  return `${normalizedDay}-${normalizedMonth}-${normalizedYear}`;
};

const isValidBulkDate = (day, month, year) => {
  const numericDay = Number(day);
  const numericMonth = Number(month);
  const numericYear = Number(year);

  if (
    !Number.isInteger(numericDay)
    || !Number.isInteger(numericMonth)
    || !Number.isInteger(numericYear)
    || numericMonth < 1
    || numericMonth > 12
    || numericDay < 1
    || numericDay > 31
  ) {
    return false;
  }

  const date = new Date(numericYear, numericMonth - 1, numericDay);
  return (
    date.getFullYear() === numericYear
    && date.getMonth() === numericMonth - 1
    && date.getDate() === numericDay
  );
};

const normalizeBulkTwoDigitYear = (year) => {
  const numericYear = Number(year);
  if (!Number.isInteger(numericYear)) {
    return year;
  }

  return numericYear <= 49 ? 2000 + numericYear : 1900 + numericYear;
};

const normalizeBulkDateValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  if (typeof value === 'number') {
    const utcDays = Math.floor(value - 25569);
    const utcValue = utcDays * 86400;
    const dateInfo = new Date(utcValue * 1000);
    const day = String(dateInfo.getUTCDate()).padStart(2, '0');
    const month = String(dateInfo.getUTCMonth() + 1).padStart(2, '0');
    const year = dateInfo.getUTCFullYear();
    return `${day}-${month}-${year}`;
  }

  if (value instanceof Date && !Number.isNaN(value.valueOf())) {
    const day = String(value.getDate()).padStart(2, '0');
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const year = value.getFullYear();
    return `${day}-${month}-${year}`;
  }

  const stringValue = toBulkString(value);
  if (/^\d{2}-\d{2}-\d{4}$/.test(stringValue)) {
    const [day, month, year] = stringValue.split('-');
    return isValidBulkDate(day, month, year) ? formatBulkDateParts(day, month, year) : stringValue;
  }

  if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
    const [year, month, day] = stringValue.split('-');
    return isValidBulkDate(day, month, year) ? formatBulkDateParts(day, month, year) : stringValue;
  }

  const slashOrDashMatch = stringValue.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{2}|\d{4})$/);
  if (slashOrDashMatch) {
    let [, firstPart, secondPart, yearPart] = slashOrDashMatch;
    const numericFirst = Number(firstPart);
    const numericSecond = Number(secondPart);
    const normalizedYear = yearPart.length === 2
      ? normalizeBulkTwoDigitYear(yearPart)
      : Number(yearPart);

    let dayPart = numericFirst;
    let monthPart = numericSecond;

    if (numericFirst <= 12 && numericSecond <= 12) {
      monthPart = numericFirst;
      dayPart = numericSecond;
    } else if (numericFirst <= 12 && numericSecond > 12) {
      monthPart = numericFirst;
      dayPart = numericSecond;
    }

    if (isValidBulkDate(dayPart, monthPart, normalizedYear)) {
      return formatBulkDateParts(dayPart, monthPart, normalizedYear);
    }
  }

  if (/^\d{4}-\d{2}-\d{2}T/.test(stringValue)) {
    const parsed = new Date(stringValue);
    if (!Number.isNaN(parsed.valueOf())) {
      const day = String(parsed.getDate()).padStart(2, '0');
      const month = String(parsed.getMonth() + 1).padStart(2, '0');
      const year = parsed.getFullYear();
      return `${day}-${month}-${year}`;
    }
  }

  const fallbackParsed = new Date(stringValue);
  if (!Number.isNaN(fallbackParsed.valueOf())) {
    const day = String(fallbackParsed.getDate()).padStart(2, '0');
    const month = String(fallbackParsed.getMonth() + 1).padStart(2, '0');
    const year = fallbackParsed.getFullYear();
    return `${day}-${month}-${year}`;
  }

  return stringValue;
};

const parseBulkDisplayDate = (value) => {
  const match = value.match(/^(\d{2})-(\d{2})-(\d{4})$/);
  if (!match) {
    return null;
  }

  const [, day, month, year] = match;
  const parsed = new Date(Number(year), Number(month) - 1, Number(day));
  if (
    Number.isNaN(parsed.valueOf())
    || parsed.getFullYear() !== Number(year)
    || parsed.getMonth() !== Number(month) - 1
    || parsed.getDate() !== Number(day)
  ) {
    return null;
  }

  return parsed;
};

const isBulkFutureDate = (value) => {
  const parsed = parseBulkDisplayDate(value);
  if (!parsed) {
    return false;
  }

  parsed.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return parsed > today;
};

const getBulkPhoneValidationMessage = getBulkNationalPhoneValidationMessage;

const getBulkValueFromRow = (row, field) => {
  const aliases = BULK_TRAINEE_FIELD_ALIASES[field] || [field];
  for (const alias of aliases) {
    if (row?.[alias] !== undefined && row?.[alias] !== null && row?.[alias] !== '') {
      return row[alias];
    }
  }
  return '';
};

export const normalizeBulkTraineeRow = (row = {}) => {
  let mobilenoCode = toBulkString(getBulkValueFromRow(row, 'mobileno_code'));
  if (mobilenoCode && !mobilenoCode.startsWith('+')) {
    mobilenoCode = `+${mobilenoCode}`;
  }

  return {
    name: toBulkString(getBulkValueFromRow(row, 'name')),
    email: toBulkString(getBulkValueFromRow(row, 'email')),
    gender: toBulkString(getBulkValueFromRow(row, 'gender')),
    date_of_birth: normalizeBulkDateValue(getBulkValueFromRow(row, 'date_of_birth')),
    password: toBulkString(getBulkValueFromRow(row, 'password')),
    mobileno_code: mobilenoCode,
    mobile: toBulkString(getBulkValueFromRow(row, 'mobile')),
    employee_no: toBulkString(getBulkValueFromRow(row, 'employee_no')),
    company_code: toBulkString(getBulkValueFromRow(row, 'company_code')),
    company_description: toBulkString(getBulkValueFromRow(row, 'company_description')),
    job_title: toBulkString(getBulkValueFromRow(row, 'job_title')),
    date_joined: normalizeBulkDateValue(getBulkValueFromRow(row, 'date_joined')),
    ic_no: toBulkString(getBulkValueFromRow(row, 'ic_no')),
    fin_no: toBulkString(getBulkValueFromRow(row, 'fin_no')),
    pwm_rank: toBulkString(getBulkValueFromRow(row, 'pwm_rank'))
  };
};

export const validateBulkTraineeRow = (row) => {
  const issues = [];
  const addIssue = (field, message) => {
    issues.push({
      field,
      label: BULK_TRAINEE_COLUMN_LOOKUP.get(field) || field,
      message
    });
  };

  if (!row.name) {
    addIssue('name', 'Name is required');
  } else if (row.name.length > 25) {
    addIssue('name', 'Name must be maximum 25 characters');
  }

  if (!row.email) {
    addIssue('email', 'Email is required');
  } else if (!BULK_TRAINEE_EMAIL_PATTERN.test(row.email)) {
    addIssue('email', 'Invalid email format');
  }

  if (!row.gender || !BULK_TRAINEE_VALID_GENDERS.includes(row.gender)) {
    addIssue('gender', 'Gender must be Male, Female, or Other');
  }

  if (!row.date_of_birth) {
    addIssue('date_of_birth', 'Date of Birth is required');
  } else if (!/^\d{2}-\d{2}-\d{4}$/.test(row.date_of_birth)) {
    addIssue('date_of_birth', 'Date of Birth must be in dd-mm-yyyy format');
  } else if (!parseBulkDisplayDate(row.date_of_birth)) {
    addIssue('date_of_birth', 'Date of Birth must be a valid calendar date');
  } else if (isBulkFutureDate(row.date_of_birth)) {
    addIssue('date_of_birth', 'Date of Birth cannot be in the future');
  }

  if (!row.password) {
    addIssue('password', 'Password is required');
  } else {
    const passwordErrors = validateSharedPassword(row.password);
    if (passwordErrors.length > 0) {
      addIssue('password', passwordErrors[0]);
    }
  }

  if (row.mobileno_code && !/^\+\d{1,4}$/.test(row.mobileno_code)) {
    addIssue('mobileno_code', 'Country Code must be 1-4 digits (e.g., 91, 1, 971, 65)');
  }

  if (row.mobile) {
    const cleanedPhone = row.mobile.replace(/[\s\-()]/g, '');
    const phoneIssue = getBulkPhoneValidationMessage(row.mobileno_code, cleanedPhone);
    if (phoneIssue) {
      addIssue('mobile', phoneIssue);
    }
  }

  if (row.date_joined) {
    if (!/^\d{2}-\d{2}-\d{4}$/.test(row.date_joined)) {
      addIssue('date_joined', 'Date Joined must be in dd-mm-yyyy format');
    } else if (!parseBulkDisplayDate(row.date_joined)) {
      addIssue('date_joined', 'Date Joined must be a valid calendar date');
    } else if (isBulkFutureDate(row.date_joined)) {
      addIssue('date_joined', 'Date Joined cannot be in the future');
    }
  }

  return {
    issues,
    issueMap: issues.reduce((acc, issue) => {
      if (!acc[issue.field]) {
        acc[issue.field] = [];
      }
      acc[issue.field].push(issue.message);
      return acc;
    }, {}),
    isValid: issues.length === 0
  };
};

export const buildBulkPreviewRows = (rows = []) => {
  return rows.map((row, index) => {
    const values = normalizeBulkTraineeRow(row);
    const validation = validateBulkTraineeRow(values);
    const rowNumber = index + BULK_TRAINEE_SERIAL_ROW_OFFSET;
    const localIssues = validation.issues.map((issue) => ({
      id: createBulkIssueId(rowNumber, issue.field, issue.message),
      rowNumber,
      field: issue.field,
      label: issue.label,
      message: issue.message,
      source: 'local'
    }));

    return {
      id: `bulk-row-${rowNumber}`,
      rowNumber,
      values,
      localIssues,
      localIssueMap: createBulkIssueMap(validation.issues),
      backendIssues: [],
      backendIssueMap: {},
      issueMap: createBulkIssueMap(validation.issues),
      issues: localIssues,
      hasIssues: !validation.isValid,
      exists: false,
      existingUserId: null
    };
  });
};

const inferIssueField = (issue = {}) => {
  if (issue.field && issue.field !== 'row') {
    return issue.field;
  }

  if (issue.column) {
    return Object.entries(BULK_TRAINEE_FIELD_ALIASES).find(([, aliases]) => aliases.includes(issue.column))?.[0] || issue.column;
  }

  const message = String(issue.message || issue.error || issue.errors?.[0] || '').toLowerCase();
  if (message.includes('email')) return 'email';
  if (message.includes('password')) return 'password';
  if (message.includes('gender')) return 'gender';
  if (message.includes('date of birth')) return 'date_of_birth';
  if (message.includes('country code')) return 'mobileno_code';
  if (message.includes('phone')) return 'mobile';
  if (message.includes('name')) return 'name';
  return 'row';
};

const normalizeBulkValidationResponse = (response) => {
  const payload = response?.data ?? response ?? {};
  const data = payload.data ?? payload.validation ?? payload.result ?? payload;
  const rows = Array.isArray(data?.rows) ? data.rows : Array.isArray(data?.data) ? data.data : null;
  const issues = Array.isArray(data?.issues)
    ? data.issues
    : Array.isArray(data?.errors)
      ? data.errors
      : [];
  const summary = data?.summary || payload.summary || {};

  return { payload, data, rows, issues, summary, success: response?.success ?? payload?.success };
};

export const isBulkPreviewCellEditable = () => true;

export const BULK_PREVIEW_FILTERS = {
  total: 'total',
  valid: 'valid',
  existing: 'existing',
  issues: 'issues'
};

export const getBulkPreviewRowStatus = (row) => {
  if (row?.hasIssues) {
    return 'issue';
  }
  if (row?.exists) {
    return 'existing';
  }
  return 'valid';
};

export const getBulkPreviewSummary = (rows = [], summary = {}) => {
  const computed = {
    total: rows.length,
    validCount: rows.filter((row) => getBulkPreviewRowStatus(row) === 'valid').length,
    existingCount: rows.filter((row) => getBulkPreviewRowStatus(row) === 'existing').length,
    invalidCount: rows.filter((row) => getBulkPreviewRowStatus(row) === 'issue').length
  };

  return {
    total: summary.total ?? computed.total,
    validCount: summary.validCount ?? computed.validCount,
    existingCount: summary.existingCount ?? computed.existingCount,
    invalidCount: summary.invalidCount ?? computed.invalidCount
  };
};

export const filterBulkPreviewRows = (rows = [], filter = BULK_PREVIEW_FILTERS.total) => {
  if (filter === BULK_PREVIEW_FILTERS.valid) {
    return rows.filter((row) => getBulkPreviewRowStatus(row) === 'valid');
  }
  if (filter === BULK_PREVIEW_FILTERS.existing) {
    return rows.filter((row) => getBulkPreviewRowStatus(row) === 'existing');
  }
  if (filter === BULK_PREVIEW_FILTERS.issues) {
    return rows.filter((row) => getBulkPreviewRowStatus(row) === 'issue');
  }
  return rows;
};

export const mergeBulkRowIssues = (rows, validationIssues, validationRows) => {
  const rowMap = new Map(rows.map((row) => [
    row.rowNumber,
    {
      ...row,
      localIssues: row.localIssues || [],
      localIssueMap: row.localIssueMap || {},
      backendIssues: [],
      backendIssueMap: {},
      exists: false,
      existingUserId: null
    }
  ]));
  const seenBackendIssueKeys = new Set();

  const applyIssue = (rowNumber, issue) => {
    const currentRow = rowMap.get(rowNumber);
    if (!currentRow) {
      return;
    }

    const field = inferIssueField(issue);
    const message = String(issue.message || issue.error || issue?.errors?.[0] || 'Invalid value');
    const label = BULK_TRAINEE_COLUMN_LOOKUP.get(field) || issue.label || field;
    const dedupeKey = `${rowNumber}|${field}|${message}`;

    if (seenBackendIssueKeys.has(dedupeKey)) {
      return;
    }
    seenBackendIssueKeys.add(dedupeKey);

    const issueEntry = {
      id: issue.id || createBulkIssueId(rowNumber, field, message),
      rowNumber,
      field,
      label,
      message,
      source: 'backend'
    };

    if (!currentRow.backendIssueMap[field]) {
      currentRow.backendIssueMap[field] = [];
    }
    currentRow.backendIssueMap[field].push(message);
    currentRow.backendIssues.push(issueEntry);
  };

  validationIssues.forEach((issue) => {
    const rowNumber = Number(issue.rowNumber ?? issue.row ?? issue.row_index ?? issue.rowNumber);
    if (!Number.isFinite(rowNumber)) {
      return;
    }
    applyIssue(rowNumber, issue);
  });

  if (Array.isArray(validationRows)) {
    validationRows.forEach((validationRow) => {
      const rowNumber = Number(validationRow.rowNumber ?? validationRow.row ?? validationRow.row_index);
      if (!Number.isFinite(rowNumber)) {
        return;
      }

      const currentRow = rowMap.get(rowNumber);
      if (!currentRow) {
        return;
      }

      if (validationRow.values || validationRow.data) {
        currentRow.values = {
          ...currentRow.values,
          ...normalizeBulkTraineeRow(validationRow.values || validationRow.data)
        };
      }

      currentRow.exists = Boolean(validationRow.exists);
      currentRow.existingUserId = validationRow.existingUserId ?? validationRow.existing_user_id ?? null;

      if (Array.isArray(validationRow.issues)) {
        validationRow.issues.forEach((issue) => applyIssue(rowNumber, issue));
      }
    });
  }

  const nextRows = Array.from(rowMap.values()).map((row) => {
    const mergedIssueMap = {};
    const mergedIssues = [...(row.localIssues || []), ...(row.backendIssues || [])];

    mergedIssues.forEach((issue) => {
      if (!mergedIssueMap[issue.field]) {
        mergedIssueMap[issue.field] = [];
      }
      mergedIssueMap[issue.field].push(issue.message);
    });

    const hasIssues = mergedIssues.length > 0;

    return {
      ...row,
      issueMap: mergedIssueMap,
      issues: mergedIssues,
      hasIssues,
      exists: Boolean(row.exists) && !hasIssues
    };
  });

  const issueList = nextRows.flatMap((row) =>
    row.issues.map((issue) => ({
      ...issue,
      rowId: row.id,
      value: row.values[issue.field] || ''
    }))
  );

  return { rows: nextRows, issueList, summary: getBulkPreviewSummary(nextRows) };
};

const TRAINEE_STATUS_FILTERS = new Set(['all', 'active', 'inactive', 'pending']);

const getTraineeStatusFilterFromSearch = (searchParams) => {
  const status = (searchParams.get('status') || '').toLowerCase();
  return TRAINEE_STATUS_FILTERS.has(status) ? status : 'all';
};

const TRAINEES_ANALYTICS_REPORT_COLUMNS = [
  { key: '_displayRowNo', label: 'No.' },
  { key: 'name', label: 'Name' },
  { key: 'email', label: 'Email' },
  { key: 'active_courses', label: 'Active courses' },
  { key: 'pending_courses', label: 'Pending courses' },
  { key: 'completed_courses', label: 'Completed courses' },
  { key: 'completed_certificates', label: 'Completed certificates' },
  { key: 'active_classrooms', label: 'Active classrooms' },
  { key: 'total_certificates', label: 'Total certificates' },
];

function Trainees() {
  const navigate = useNavigate();
  const { timeZone } = useTimezone();
  const [searchParams, setSearchParams] = useSearchParams();
  const { permissions } = usePermissions();
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [traineeToDelete, setTraineeToDelete] = useState(null);
  const activeFilter = getTraineeStatusFilterFromSearch(searchParams);
  const [mode, setMode] = useState('Create'); // 'Create' or 'Edit'
  const [selectedTrainee, setSelectedTrainee] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [trainees, setTrainees] = useState([]);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 0,
    totalCount: 0
  });

  const [formData, setFormData] = useState({
    profileImage: null,
    profile_pic_url: '',
    upload_image_url: null,
    name: '',
    email: '',
    gender: '',
    dateOfBirth: '',
    phoneNumber: '',
    countryCode: DEFAULT_TRAINEE_PHONE_CODE,
    employeeNo: '',
    companyCode: '',
    companyDescription: '',
    jobTitle: '',
    dateJoined: '',
    icNo: '',
    finNo: '',
    pwmRank: '',
    password: ''
  });

  const [errors, setErrors] = useState({});
  const [imagePreview, setImagePreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  // Bulk upload modal states
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkUploadFile, setBulkUploadFile] = useState(null);
  const [bulkUploadRows, setBulkUploadRows] = useState([]);
  const [bulkUploadSummary, setBulkUploadSummary] = useState({
    total: 0,
    validCount: 0,
    existingCount: 0,
    invalidCount: 0
  });
  const [bulkUploadResult, setBulkUploadResult] = useState(null);
  const [bulkUploadSelectedRowNumber, setBulkUploadSelectedRowNumber] = useState(null);
  const [bulkPreviewFilter, setBulkPreviewFilter] = useState(BULK_PREVIEW_FILTERS.total);
  const [bulkUploadServerError, setBulkUploadServerError] = useState('');
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);
  const [bulkCheckMode, setBulkCheckMode] = useState(null); // 'initial' or 'verify'
  const [bulkIsVerified, setBulkIsVerified] = useState(false);
  const [bulkHasCurrentServerCheck, setBulkHasCurrentServerCheck] = useState(false);
  const [bulkUploadStep, setBulkUploadStep] = useState('upload'); // 'upload', 'preview', 'importing', 'result'
  const bulkFileInputRef = useRef(null);
  const bulkValidationRequestRef = useRef(0);
  const traineeFormRef = useRef(null);
  const [showTraineesReportModal, setShowTraineesReportModal] = useState(false);
  const [traineesReportLoading, setTraineesReportLoading] = useState(false);
  const [traineesReportRows, setTraineesReportRows] = useState([]);
  const selectedCountry = getCountryByCode(formData.countryCode);
  const phoneRule = getPhoneRuleFromCountry(selectedCountry);

  // Debounced search term
  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  const getTraineeFormErrors = (values, currentMode) => {
    const newErrors = {};
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

    // Required fields
    if (!values.name.trim()) newErrors.name = 'Name is required';
    if (values.name.length > 25) newErrors.name = 'Name must be less than 25 characters';
    if (!values.email.trim()) newErrors.email = 'Email is required';
    if (values.email && !emailRegex.test(values.email)) newErrors.email = 'Invalid email format';
    if (!values.gender) newErrors.gender = 'Gender is required';
    const dateOfBirthErrors = validateDateInput(values.dateOfBirth, { label: 'Date of Birth', required: true });
    if (dateOfBirthErrors.length > 0) newErrors.dateOfBirth = dateOfBirthErrors[0];
    const dateJoinedErrors = validateDateInput(values.dateJoined, { label: 'Date Joined', required: false });
    if (dateJoinedErrors.length > 0) newErrors.dateJoined = dateJoinedErrors[0];

    // Password validation (required for create, optional for edit) — shared rules with Roles & Access / backend
    if (currentMode === 'Create' && !values.password) {
      newErrors.password = 'Password is required';
    } else if (values.password) {
      const passwordErrors = validateSharedPassword(values.password);
      if (passwordErrors.length > 0) {
        newErrors.password = passwordErrors[0];
      }
    }

    // Optional field validations
    if (values.phoneNumber) {
      const phoneErrs = validateNationalPhoneDigits(
        values.phoneNumber.trim(),
        values.countryCode,
        { required: false },
      );
      if (phoneErrs.length > 0) {
        newErrors.phoneNumber = phoneErrs[0];
      }
    }
    if (values.employeeNo.length > 50) newErrors.employeeNo = 'Employee No must be less than 50 characters';
    if (values.companyCode.length > 50) newErrors.companyCode = 'Company Code must be less than 50 characters';
    if (values.companyDescription.length > 255) newErrors.companyDescription = 'Company Description must be less than 255 characters';
    if (values.pwmRank.length > 1000) newErrors.pwmRank = 'PWM Rank must be less than 1000 characters';
    if (values.jobTitle.length > 1000) newErrors.jobTitle = 'Job Title must be less than 1000 characters';

    return newErrors;
  };

  const scrollToFirstTraineeError = (nextErrors) => {
    const [firstField] = Object.keys(nextErrors);
    if (!firstField || !traineeFormRef.current) {
      return;
    }

    const field = traineeFormRef.current.querySelector(`[name="${firstField}"]`);
    if (field) {
      field.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (typeof field.focus === 'function') {
        field.focus();
      }
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const nextFormData = { ...formData };

    if (name === 'phoneNumber') {
      nextFormData.phoneNumber = value.replace(/\D/g, '').slice(0, phoneRule.max);
    } else {
      nextFormData[name] = value;
    }

    setFormData(nextFormData);

    if (errors[name] || name === 'dateOfBirth' || name === 'dateJoined' || name === 'password') {
      const nextErrors = getTraineeFormErrors(nextFormData, mode);
      setErrors((prev) => ({
        ...prev,
        [name]: nextErrors[name] || ''
      }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        profileImage: file,
        upload_image_url: file
      }));

      // Create preview URL
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Fetch trainees data
  const fetchTraineesData = useCallback(async () => {
    try {
      setLoading(true);
      const status = activeFilter === 'all' ? null : activeFilter === 'inactive' ? 'blocked' : activeFilter;
      const response = await getTraineeDetails({
        search: debouncedSearchTerm,
        page: pagination.page,
        limit: pagination.limit,
        status: status
      });


      if (response.success) {
        // Support both legacy shape: { data: { data, pagination } }
        // and new standardized shape: { data: { trainees, pagination } }
        const payload = response.data || {};
        const traineesList = Array.isArray(payload.data) ? payload.data : payload.trainees;
        const paginationData = payload.pagination || {};

        // Support both naming styles for pagination fields
        const pageVal = paginationData.page ?? paginationData.currentPage ?? 1;
        const limitVal = paginationData.limit ?? paginationData.itemsPerPage ?? pagination.limit;
        const totalPagesVal = paginationData.totalPages ?? 0;
        const totalCountVal = paginationData.totalCount ?? paginationData.totalItems ?? paginationData.total ?? 0;

        setTrainees(traineesList || []);
        setPagination({
          page: pageVal,
          limit: limitVal,
          totalPages: totalPagesVal,
          totalCount: totalCountVal
        });
      } else {
        toast.error("Failed to fetch trainee data");
        setTrainees([]);
      }
    } catch (error) {
      console.error("Error fetching trainee data:", error);
      toast.error("Something went wrong");
      setTrainees([]);
    } finally {
      setLoading(false);
    }
  }, [activeFilter, debouncedSearchTerm, pagination.page, pagination.limit]);

  // Reset form data
  const resetForm = () => {
    setFormData({
      profileImage: null,
      profile_pic_url: '',
      upload_image_url: null,
      name: '',
      email: '',
      gender: '',
      dateOfBirth: '',
      phoneNumber: '',
      countryCode: DEFAULT_TRAINEE_PHONE_CODE,
      employeeNo: '',
      companyCode: '',
      companyDescription: '',
      jobTitle: '',
      dateJoined: '',
      icNo: '',
      finNo: '',
      pwmRank: '',
      password: ''
    });
    setImagePreview(null);
    setErrors({});
    setShowPassword(false);
  };

  // Handle modal show for create/edit
  const handleShowModal = async (modeType, trainee = null) => {
    if (modeType === 'Create' && !permissions.trainee_management_create) {
      toast.warning("You don't have permission to create trainees");
      return;
    }
    if (modeType === 'Edit' && !permissions.trainee_management_edit) {
      toast.warning("You don't have permission to edit trainees");
      return;
    }
    setMode(modeType);
    setSelectedTrainee(trainee);
    setShowModal(true);

    if (modeType === 'Edit' && trainee) {
      resetForm();
      try {
        setLoading(true);
        const response = await fetchUserDetails(trainee.id);

        if (response.success && response.data) {
          const mapped = mapTraineeApiDetailToFormFields(response.data);
          if (mapped) {
            const { previewUrl, ...formFields } = mapped;
            setFormData({ ...formFields, password: '' });
            setImagePreview(previewUrl || null);
          }
        } else {
          toast.error("Failed to fetch user details");
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
        toast.error("Something went wrong");
      } finally {
        setLoading(false);
      }
    } else {
      resetForm();
    }
  };

  // Handle modal close
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTrainee(null);
    resetForm();
    setMode('Create');
  };

  // Handle save (create/edit)
  const handleSave = async () => {
    try {
      const nextErrors = getTraineeFormErrors(formData, mode);
      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors);
        scrollToFirstTraineeError(nextErrors);
        return;
      }

      setLoading(true);
      const formDataToSend = new FormData();

      // Append basic fields
      formDataToSend.append("name", formData.name);
      formDataToSend.append("email", formData.email);
      formDataToSend.append("mobileno_code", formData.countryCode);
      formDataToSend.append("mobile", formData.phoneNumber);
      formDataToSend.append("gender", formData.gender);
      formDataToSend.append("date_of_birth", formData.dateOfBirth);

      // Password handling
      if (mode === 'Create' || (mode === 'Edit' && formData.password.trim() !== "")) {
        formDataToSend.append("password", formData.password);
      }

      // Optional fields
      formDataToSend.append("employee_no", formData.employeeNo || "");
      formDataToSend.append("company_code", formData.companyCode || "");
      formDataToSend.append("company_description", formData.companyDescription || "");
      formDataToSend.append("ic_no", formData.icNo || "");
      formDataToSend.append("fin_no", formData.finNo || "");
      formDataToSend.append("date_joined", formData.dateJoined || "");
      formDataToSend.append("pwm_rank", formData.pwmRank || "");
      formDataToSend.append("job_title", formData.jobTitle || "");

      let response;
      if (mode === 'Edit' && selectedTrainee) {
        // Handle image for edit
        if (formData.upload_image_url !== null && formData.upload_image_url !== undefined) {
          formDataToSend.append("profile_pic", formData.upload_image_url);
        }
        response = await editTraineeDetails(selectedTrainee.id, formDataToSend);
      } else {
        // Handle image for create
        if (formData.upload_image_url !== null) {
          formDataToSend.append("profile_pic", formData.upload_image_url);
        } else if (formData.profile_pic_url && formData.profile_pic_url.trim() !== '') {
          formDataToSend.append("profile_pic_url", formData.profile_pic_url);
        }
        response = await createNewOrganisationUser(formDataToSend);
      }

      if (response.success) {
        toast.success(mode === 'Edit' ? "Trainee updated successfully" : "Trainee created successfully");
        handleCloseModal();
        fetchTraineesData();
      } else {
        const message = friendlyMessage(response?.message || response?.error?.message, 'Unable to save trainee. Please try again.');
        if (mode === 'Create' && /email already exists/i.test(message)) {
          setErrors((prev) => ({ ...prev, email: message }));
          scrollToFirstTraineeError({ email: message });
        }
        toast.error(message);
      }
    } catch (error) {
      console.error("Error saving trainee:", error);
      const message = friendlyMessage(error?.message, 'Unable to save trainee. Please try again.');
      if (mode === 'Create' && /email already exists/i.test(message)) {
        setErrors((prev) => ({ ...prev, email: message }));
        scrollToFirstTraineeError({ email: message });
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const openDeleteTraineeModal = (trainee) => {
    if (!permissions.trainee_management_delete) {
      toast.warning("You don't have permission to delete trainees");
      return;
    }
    setTraineeToDelete(trainee);
    setShowDeleteModal(true);
  };

  const closeDeleteTraineeModal = () => {
    setShowDeleteModal(false);
    setTraineeToDelete(null);
  };

  const confirmDeleteTrainee = async () => {
    if (!traineeToDelete?.id) {
      closeDeleteTraineeModal();
      return;
    }
    try {
      setLoading(true);
      const response = await deleteTrainee(traineeToDelete.id);

      if (response.success) {
        toast.success("Trainee deleted successfully");
        closeDeleteTraineeModal();
        fetchTraineesData();
      } else {
        toast.error("Failed to delete trainee");
      }
    } catch (error) {
      console.error("Error deleting trainee:", error);
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // Handle status toggle
  const handleStatusToggle = async (trainee) => {
    if (!permissions.trainee_management_status) {
      toast.warning("You don't have permission to change trainee status");
      return;
    }
    const traineeId = trainee.id;
    try {
      const newStatus = trainee.is_blocked ? 0 : 1;
      const response = await activeInactiveTrainee(traineeId, newStatus);

      if (response.success) {
        await fetchTraineesData();
        toast.success(`Trainee ${newStatus === 0 ? 'unblocked' : 'blocked'} successfully`);
      } else {
        toast.error("Failed to update status");
      }
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Something went wrong");
    }
  };

  const resetBulkUploadState = () => {
    bulkValidationRequestRef.current += 1;
    setBulkUploadFile(null);
    setBulkUploadRows([]);
    setBulkUploadSummary({
      total: 0,
      validCount: 0,
      existingCount: 0,
      invalidCount: 0
    });
    setBulkUploadResult(null);
    setBulkUploadSelectedRowNumber(null);
    setBulkPreviewFilter(BULK_PREVIEW_FILTERS.total);
    setBulkUploadServerError('');
    setBulkUploadLoading(false);
    setBulkCheckMode(null);
    setBulkIsVerified(false);
    setBulkHasCurrentServerCheck(false);
    setBulkUploadStep('upload');
  };

  const handleUploadClick = () => {
    if (!permissions.trainee_management_create) {
      toast.warning("You don't have permission to bulk upload trainees");
      return;
    }

    setShowBulkUploadModal(true);
    resetBulkUploadState();
  };

  const handleCloseBulkUploadModal = () => {
    setShowBulkUploadModal(false);
    resetBulkUploadState();
  };

  // Download template
  const handleDownloadTemplate = () => {
    const link = document.createElement('a');
    link.href = '/bulk_user_registration_template.csv';
    link.download = 'bulk_user_registration_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const parseBulkUploadFile = async (file) => {
    const fileExtension = `.${file.name.split('.').pop().toLowerCase()}`;
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv'
    ];

    if (!validTypes.includes(file.type) && !['.xlsx', '.xls', '.csv'].includes(fileExtension)) {
      throw new Error('Please select only Excel (.xlsx, .xls) or CSV files');
    }

    const rows = await readSpreadsheetRowsFromFile(file);
    return rows;
  };

  const syncBulkPreviewRows = (rows, validationResponse = null) => {
    const previewRows = buildBulkPreviewRows(rows);
    const { rows: mergedRows } = mergeBulkRowIssues(
      previewRows,
      validationResponse?.issues || [],
      validationResponse?.rows || null
    );

    setBulkUploadRows(mergedRows);
    setBulkUploadSummary(getBulkPreviewSummary(mergedRows, validationResponse?.summary));
    setBulkUploadSelectedRowNumber(mergedRows[0]?.rowNumber || null);
    setBulkPreviewFilter(BULK_PREVIEW_FILTERS.total);
    setBulkUploadServerError('');
    setBulkUploadStep('preview');
  };

  const applyMergedBulkPreview = (merged, summaryOverride = {}) => {
    setBulkUploadRows(merged.rows);
    setBulkUploadSummary(getBulkPreviewSummary(merged.rows, summaryOverride));
    setBulkUploadSelectedRowNumber((current) => {
      if (current && merged.rows.some((row) => row.rowNumber === current)) {
        return current;
      }
      return merged.rows[0]?.rowNumber || null;
    });
    setBulkUploadServerError('');
  };

  const validateBulkPreviewWithBackend = async (rows, { quiet = false, applyResult = true, reportError = true } = {}) => {
    const requestId = ++bulkValidationRequestRef.current;
    const payload = rows.map((row) => row.values || row);

    try {
      const response = await bulkValidateUsersJSON(payload);
      if (requestId !== bulkValidationRequestRef.current) {
        return null;
      }

      const normalized = normalizeBulkValidationResponse(response);
      const validationRows = normalized.rows || [];
      const validationIssues = normalized.issues || [];
      const merged = mergeBulkRowIssues(rows, validationIssues, validationRows);
      if (applyResult) applyMergedBulkPreview(merged, normalized.summary);
      return { ...normalized, merged };
    } catch (error) {
      if (requestId !== bulkValidationRequestRef.current) {
        return null;
      }

      const message = friendlyMessage(error?.message, 'Unable to validate trainees right now. Please try again.');
      if (reportError) setBulkUploadServerError(message);
      if (!quiet) {
        toast.error(message);
      }
      return null;
    }
  };

  const handleBulkFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    const selectionId = ++bulkValidationRequestRef.current;

    setBulkUploadFile(file);
    setBulkUploadLoading(true);
    setBulkCheckMode('initial');
    setBulkUploadStep('preview');
    setBulkUploadServerError('');
    setBulkIsVerified(false);
    setBulkHasCurrentServerCheck(false);
    setBulkPreviewFilter(BULK_PREVIEW_FILTERS.total);
    e.target.value = '';

    try {
      const jsonData = await parseBulkUploadFile(file);
      if (selectionId !== bulkValidationRequestRef.current) return;
      const localRows = buildBulkPreviewRows(jsonData);
      setBulkUploadRows(localRows);

      const [remoteValidation] = await Promise.all([
        validateBulkPreviewWithBackend(localRows, { quiet: true, applyResult: false, reportError: false }),
        new Promise((resolve) => setTimeout(resolve, 600))
      ]);
      if (selectionId + 1 !== bulkValidationRequestRef.current) return;
      if (remoteValidation && Array.isArray(remoteValidation.rows) && remoteValidation.rows.length === localRows.length) {
        applyMergedBulkPreview(remoteValidation.merged, remoteValidation.summary);
        setBulkHasCurrentServerCheck(true);
        const summary = getBulkPreviewSummary(
          remoteValidation.merged?.rows || localRows,
          remoteValidation.summary
        );
        setBulkUploadStep('preview');
        setBulkUploadSummary(summary);
      } else {
        syncBulkPreviewRows(jsonData);
        setBulkUploadServerError('Server verification is unavailable. You can edit the preview, then try Verify changes again.');
      }
    } catch (error) {
      if (selectionId !== bulkValidationRequestRef.current && selectionId + 1 !== bulkValidationRequestRef.current) return;
      const message = error.message || 'Error reading file. Please check the file format.';
      console.error('Error parsing bulk upload file:', error);
      toast.error(message);
      resetBulkUploadState();
      setBulkUploadStep('upload');
    } finally {
      if (selectionId === bulkValidationRequestRef.current || selectionId + 1 === bulkValidationRequestRef.current) {
        setBulkUploadLoading(false);
        setBulkCheckMode(null);
      }
    }
  };

  const handleBulkCellChange = (rowNumber, field, value) => {
    if (bulkUploadLoading) return;

    // Any edit makes the previous server lookup and import approval stale.
    bulkValidationRequestRef.current += 1;
    setBulkIsVerified(false);
    setBulkHasCurrentServerCheck(false);
    setBulkUploadServerError('');
    setBulkPreviewFilter(BULK_PREVIEW_FILTERS.total);

    const nextRows = bulkUploadRows.map((row) => {
      const values = row.rowNumber === rowNumber
        ? { ...row.values, [field]: value }
        : row.values;
      const validation = validateBulkTraineeRow(normalizeBulkTraineeRow(values));
      const localIssues = validation.issues.map((issue) => ({
        id: createBulkIssueId(row.rowNumber, issue.field, issue.message),
        rowNumber: row.rowNumber,
        field: issue.field,
        label: issue.label,
        message: issue.message,
        source: 'local'
      }));

      return {
        ...row,
        values,
        localIssues,
        localIssueMap: validation.issueMap,
        backendIssues: [],
        backendIssueMap: {},
        issues: localIssues,
        issueMap: validation.issueMap,
        hasIssues: !validation.isValid,
        exists: false,
        existingUserId: null
      };
    });

    setBulkUploadRows(nextRows);
    setBulkUploadSummary(getBulkPreviewSummary(nextRows));
  };

  const handleBulkVerify = async () => {
    if (bulkUploadLoading || !bulkUploadRows.length) return;
    const verificationId = bulkValidationRequestRef.current + 1;

    setBulkUploadLoading(true);
    setBulkCheckMode('verify');
    setBulkIsVerified(false);
    setBulkHasCurrentServerCheck(false);
    setBulkUploadServerError('');
    try {
      const [result] = await Promise.all([
        validateBulkPreviewWithBackend(bulkUploadRows, { quiet: true, applyResult: false, reportError: false }),
        new Promise((resolve) => setTimeout(resolve, 2000))
      ]);
      if (verificationId !== bulkValidationRequestRef.current) return;
      if (!result) {
        setBulkUploadServerError('Unable to verify trainees right now. Please try again.');
        return;
      }

      if (!Array.isArray(result.rows) || result.rows.length !== bulkUploadRows.length) {
        setBulkUploadServerError('Verification returned incomplete rows. Please try again.');
        return;
      }

      applyMergedBulkPreview(result.merged, result.summary);
      setBulkHasCurrentServerCheck(true);

      if (result.success !== true || result.summary.invalidCount > 0 || result.merged.summary.invalidCount > 0) {
        if (result.merged.summary.invalidCount === 0) {
          setBulkUploadServerError('Verification did not approve these rows. Please try again.');
        }
        toast.warning(result.merged.summary.invalidCount > 0
          ? `Fix ${result.merged.summary.invalidCount} row(s) with issues, then verify again.`
          : 'Verification could not approve these rows. Please try again.');
        return;
      }

      setBulkIsVerified(true);
      toast.success('All rows verified. You can now import trainees.');
    } finally {
      if (verificationId === bulkValidationRequestRef.current) {
        setBulkUploadLoading(false);
        setBulkCheckMode(null);
      }
    }
  };

  const handleBulkUploadSubmit = async () => {
    if (bulkUploadLoading || !bulkIsVerified || !bulkHasCurrentServerCheck) {
      toast.error('Verify the latest changes before importing');
      return;
    }
    if (!bulkUploadRows.length) {
      toast.error('No data to upload');
      return;
    }

    if (bulkUploadSummary.invalidCount > 0) {
      toast.error('Please fix all validation errors before importing');
      return;
    }

    setBulkUploadLoading(true);
    setBulkUploadStep('importing');
    setBulkUploadServerError('');

    try {
      const validationResult = await validateBulkPreviewWithBackend(bulkUploadRows, { quiet: false });
      if (!validationResult || !Array.isArray(validationResult.rows)
        || validationResult.rows.length !== bulkUploadRows.length
        || validationResult.success !== true
        || validationResult.summary.invalidCount > 0
        || validationResult.merged.summary.invalidCount > 0) {
        setBulkIsVerified(false);
        if (!validationResult || !Array.isArray(validationResult.rows)) setBulkHasCurrentServerCheck(false);
        toast.error('Please fix all validation errors before importing');
        setBulkUploadStep('preview');
        return;
      }

      const response = await bulkUploadUsersJSON(bulkUploadRows.map((row) => row.values));
      const importSummary = response?.data?.data || response?.data || response || {};
      const importedCount = importSummary.registered_count ?? importSummary.importedCount ?? 0;
      const updatedCount = importSummary.updated_count ?? importSummary.updatedCount ?? 0;

      if (response?.success) {
        setBulkUploadResult({ importedCount, updatedCount });
        setBulkUploadStep('result');
        setBulkUploadServerError('');
        fetchTraineesData();
        toast.success(
          importSummary.message
          || `Successfully imported ${importedCount} and updated ${updatedCount} trainee(s).`
        );
      } else {
        const message = friendlyMessage(importSummary.message || response?.message, 'Unable to import trainees. Please try again or contact support.');
        setBulkUploadServerError(message);
        toast.error(message);
        setBulkUploadStep('preview');
        setBulkIsVerified(false);
      }
    } catch (error) {
      const message = friendlyMessage(error?.message, 'Unable to import trainees because of a server error. Please try again or contact support.');
      console.error('Bulk upload error:', error);
      setBulkUploadServerError(message);
      toast.error(message);
      setBulkUploadStep('preview');
      setBulkIsVerified(false);
    } finally {
      setBulkUploadLoading(false);
    }
  };

  const handleBulkPreviewRowClick = (rowNumber) => {
    setBulkUploadSelectedRowNumber(rowNumber);
  };

  const bulkFilteredRows = useMemo(
    () => filterBulkPreviewRows(bulkUploadRows, bulkPreviewFilter),
    [bulkUploadRows, bulkPreviewFilter]
  );

  const handleBulkPreviewFilterChange = (filter) => {
    setBulkPreviewFilter(filter);
    const nextRows = filterBulkPreviewRows(bulkUploadRows, filter);
    const nextSelected = nextRows.some((row) => row.rowNumber === bulkUploadSelectedRowNumber)
      ? bulkUploadSelectedRowNumber
      : nextRows[0]?.rowNumber || null;
    setBulkUploadSelectedRowNumber(nextSelected);
  };

  const bulkSelectedRow = bulkFilteredRows.find((row) => row.rowNumber === bulkUploadSelectedRowNumber)
    || bulkFilteredRows[0]
    || null;

  const bulkHasBlockingIssues = bulkUploadSummary.invalidCount > 0;
  const bulkIsImportDisabled = bulkUploadLoading || !bulkIsVerified || !bulkHasCurrentServerCheck || bulkHasBlockingIssues || bulkUploadRows.length === 0;
  const renderBulkPreviewCell = (row, column) => {
    const fieldIssues = row.issueMap?.[column.key] || [];
    const value = row.values?.[column.key] ?? '';
    const commonProps = {
      value,
      disabled: bulkUploadLoading,
      'aria-label': `${column.label} for row ${row.rowNumber}`,
      'aria-invalid': fieldIssues.length > 0,
      onChange: (event) => handleBulkCellChange(row.rowNumber, column.key, event.target.value),
      className: `tw-w-full tw-rounded-lg tw-border tw-bg-white tw-px-3 tw-py-1.5 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-blue-300 ${fieldIssues.length ? 'tw-border-rose-400' : 'tw-border-slate-200'}`
    };

    if (column.key === 'gender') {
      return <select {...commonProps}><option value="">Select gender</option>{BULK_TRAINEE_VALID_GENDERS.map((gender) => <option key={gender} value={gender}>{gender}</option>)}</select>;
    }

    return <input {...commonProps} type={column.key === 'password' ? 'password' : column.key === 'email' ? 'email' : 'text'} autoComplete="off" placeholder={column.key.startsWith('date_') ? 'dd-mm-yyyy' : column.label} />;
  };

  // Use effect for initial data fetch and search
  useEffect(() => {
    fetchTraineesData();
  }, [fetchTraineesData]);

  // Filter trainees based on status
  const filteredTrainees = trainees;

  const handleStatusFilterChange = (nextFilter) => {
    const status = TRAINEE_STATUS_FILTERS.has(nextFilter) ? nextFilter : 'all';
    const nextParams = new URLSearchParams(searchParams);

    if (status === 'all') {
      nextParams.delete('status');
    } else {
      nextParams.set('status', status);
    }

    setPagination((prev) => ({ ...prev, page: 1 }));
    setSearchParams(nextParams, { replace: true });
  };

  const handleAnalytics = (traineeId) => {
    if (!permissions.trainee_management_view_analytics) {
      toast.warning("You don't have permission to view trainee analytics");
      return;
    }
    const encodedTraineeId = encodeData(traineeId.toString()); // Convert ID to string before encoding
    // Navigate to trainee analytics page with the trainee ID
    navigate(`/admin/traineesAnalytics/${encodedTraineeId}`);
  };

  const canRunTraineesReport = Boolean(
    permissions.trainee_management_view || permissions.trainee_management_create
  );

  const handleCloseTraineesReportModal = () => {
    setShowTraineesReportModal(false);
    setTraineesReportRows([]);
  };

  const handleTraineesReportDownloadExcel = async () => {
    if (!traineesReportRows.length) {
      return;
    }
    const excelRows = traineesReportRows.map((r, i) => {
      const row = {};
      TRAINEES_ANALYTICS_REPORT_COLUMNS.forEach(({ key, label }) => {
        if (key === '_displayRowNo') {
          row[label] = i + 1;
        } else {
          row[label] = r[key] ?? '';
        }
      });
      return row;
    });
    const wb = createWorkbook();
    addJsonSheet(wb, 'Trainees analytics', excelRows);
    const filename = `trainees-analytics-${new Date().toISOString().slice(0, 10)}.xlsx`;
    await downloadWorkbook(wb, filename);
  };

  const handleTraineesReportClick = async () => {
    if (!canRunTraineesReport) {
      toast.warning("You don't have permission to run this report");
      return;
    }
    setTraineesReportLoading(true);
    try {
      const response = await getTraineesAnalytics();
      if (response?.success && Array.isArray(response.data?.trainees)) {
        setTraineesReportRows(response.data.trainees);
        setShowTraineesReportModal(true);
      } else {
        toast.error(response?.message || 'Failed to load trainee report');
      }
    } catch (error) {
      const message =
        error?.response?.data?.error?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'Failed to load trainee report';
      toast.error(message);
    } finally {
      setTraineesReportLoading(false);
    }
  };

  const traineePasswordRequirementState = getPasswordRequirementState(formData.password);

  return (
    <>
      {/* Search, Filter and Add Trainee Button */}
      <div className="tw-w-full tw-mb-4">
        <div className="tw-min-w-0 tw-w-full">
          <div className="app-list-toolbar">

            <div className="app-list-toolbar__search">
              <Search
                type="text"
                className={"[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input"}
                placeholder="Search trainees..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPagination(prev => ({ ...prev, page: 1 }));
                }}
              />
            </div>

            <div className="app-list-toolbar__actions">
              <select
                className={"[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [appearance:none] tw-pe-[2.25rem] tw-bg-[url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPScyMCcgaGVpZ2h0PScyMCcgZmlsbD0nbm9uZScgc3Ryb2tlPScjNjQ3NDhiJyB2aWV3Qm94PScwIDAgMjQgMjQnPjxwYXRoIGQ9J203IDEwIDUgNSA1LTUnLz48L3N2Zz4=)] tw-bg-[right_.65rem_center] tw-bg-no-repeat [&[multiple]]:tw-bg-[none] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-select"}
                value={activeFilter}
                onChange={(e) => handleStatusFilterChange(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="all">All Trainees</option>
                <option value="active">Active</option>
                <option value="pending">Pending Verification</option>
                                <option value="inactive">Blocked</option>
              </select>


              {/* Bulk upload button */}
              <button
                style={{ width: '100%' }}
                className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary"}
                onClick={handleUploadClick}
                disabled={loading}
              >
                <Icon icon="fluent:arrow-upload-24-regular" width="16" height="16" className="tw-me-1" />
                Bulk Upload
              </button>

              <button
                type="button"
                style={{ width: '100%' }}
                className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary"}
                onClick={handleTraineesReportClick}
                disabled={loading || traineesReportLoading || !canRunTraineesReport}
                aria-label="Trainees analytics report"
              >
                {traineesReportLoading ? (
                  <>
                    <span className="tw-text-[#3152e8] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm tw-me-2" role="status" aria-hidden="true" />
                    Loading…
                  </>
                ) : (
                  <>
                    <Icon icon="fluent:document-data-24-regular" width="16" height="16" className="tw-me-1" />
                    Report
                  </>
                )}
              </button>

              <button
                className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary"}
                onClick={() => handleShowModal('Create')}
                disabled={loading || !permissions.trainee_management_create}
                style={{ width: '100%' }}
              >
                <Icon icon="fluent:add-24-regular" width="20" height="20" className="tw-me-2" />
                Add Trainee
              </button>
            </div>



          </div>
        </div>
      </div>

      {/* Table */}
      <div className={"tw-flex tw-flex-wrap [--grid-x:1.5rem] [--grid-y:0rem] tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] ui-row tw-mb-4"}>
        <div className="tw-min-w-[0px] ui-column tw-w-full">
          <div className={"[&_>_.ui-card-header.permission-toolbar]:tw-sticky [&_>_.ui-card-header.permission-toolbar]:tw-top-[var(--permissions-header-offset,_0px)] [&_>_.ui-card-header.permission-toolbar]:tw-z-[10] [&_>_.ui-card-header.permission-toolbar]:[background:var(--bg-primary,_#fff)] [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[1px] [&_>_.ui-card-header.permission-toolbar]:tw-border-solid [&_>_.ui-card-header.permission-toolbar]:tw-border-b-[var(--border-color,_#dee2e6)] [&_>_.ui-card-header.permission-toolbar]:[background-clip:border-box] [&_>_.ui-card-header.permission-toolbar]:tw-shadow-[0_3px_6px_rgba(0,_0,_0,_0.06)] [&.card-hover-primary:hover]:tw-border-[var(--primary-color)] [&.card-hover-success:hover]:tw-border-[var(--success-color)] [&.card-hover-danger:hover]:tw-border-[var(--danger-color)] [[data-theme='dark']_&]:tw-bg-[var(--bg-primary)] [[data-theme='dark']_&]:tw-border-[var(--border-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] tw-border-solid tw-rounded-[0.75rem] [transition:all_0.3s_ease-in-out] tw-relative tw-flex tw-min-w-[0px] tw-flex-col tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-shadow:0_2px_8px_-4px_rgb(15_23_42_/_.12)] [--tw-shadow-colored:0_2px_8px_-4px_var(--tw-shadow-color)] ui-card"}>
            <div className="tw-w-full tw-overflow-x-auto tw-rounded-[0.75rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [-webkit-overflow-scrolling:touch] ui-table-scroll">
              <Table className={"[&_.deleted-accounts-actions_.ui-button-sm]:!tw-w-auto [&_.deleted-accounts-actions_.ui-button-sm]:!tw-h-auto [&_.deleted-accounts-actions_.ui-button-sm]:!tw-min-h-[32px] [&_.deleted-accounts-actions_.ui-button-sm]:!tw-p-[0.45rem_1.25rem] [&_.deleted-accounts-actions_.ui-button-sm]:!tw-inline-flex [&_.deleted-accounts-actions_.ui-button-sm]:!tw-items-center [&_.deleted-accounts-actions_.ui-button-sm]:!tw-justify-center [&_.deleted-accounts-actions_.btn-view]:!tw-min-w-[36px] [&_.deleted-accounts-actions_.btn-view]:!tw-p-[0.45rem_0.7rem] [&_.deleted-accounts-actions_.btn-restore]:!tw-min-w-[92px] [&_.deleted-accounts-actions_.btn-restore]:!tw-pl-[1.35rem] [&_.deleted-accounts-actions_.btn-restore]:!tw-pr-[1.35rem] [&_.deleted-accounts-actions_.btn-delete]:!tw-min-w-[92px] [&_.deleted-accounts-actions_.btn-delete]:!tw-pl-[1.35rem] [&_.deleted-accounts-actions_.btn-delete]:!tw-pr-[1.35rem] tw-w-full tw-border-collapse tw-text-left tw-text-[0.875rem] tw-leading-[1.25rem] [&_>_thead]:[--tw-bg-opacity:1] [&_>_thead]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&_th]:tw-whitespace-nowrap [&_th]:[--tw-border-opacity:1] [&_th]:tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [&_th]:tw-pl-4 [&_th]:tw-pr-4 [&_th]:tw-pt-3 [&_th]:tw-pb-3 [&_th]:tw-text-[0.75rem] [&_th]:tw-leading-[1rem] [&_th]:tw-font-semibold [&_th]:[--tw-text-opacity:1] [&_th]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [&_td]:[--tw-border-opacity:1] [&_td]:tw-border-[rgb(241_245_249_/_var(--tw-border-opacity,_1))] [&_td]:tw-pl-4 [&_td]:tw-pr-4 [&_td]:tw-pt-[0.875rem] [&_td]:tw-pb-[0.875rem] [&_td]:tw-align-middle [&_td]:[--tw-text-opacity:1] [&_td]:tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&_tbody_tr:last-child_td]:tw-border-b-[0px] [&_tbody_tr]:[transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [&_tbody_tr]:[transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [&_tbody_tr]:[transition-duration:150ms] [&_tbody_tr:hover]:[--tw-bg-opacity:1] [&_tbody_tr:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&_th]:tw-border-[0px] [&_td]:tw-border-[0px] ui-table ui-table-borderless tw-mb-0"}>
                <TableHeader>
                  <tr>
                    <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500', width: '80px' }}>SL No.</th>
                    <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500', minWidth: '240px' }}>Trainee</th>
                    <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500', minWidth: '140px' }}>Learning</th>
                    <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500', minWidth: '120px' }}>Added</th>
                    <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500', width: '120px' }}>Status</th>
                    <th style={{ backgroundColor: '#f8f9fa', fontWeight: '500', width: '150px' }}>Actions</th>
                  </tr>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="tw-text-center tw-py-6">
                        <div className="!tw-text-[var(--primary-color)] tw-w-[1.5em] tw-h-[1.5em] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] ui-spinner tw-text-brand-600" role="status">
                          <span className="tw-sr-only">Loading...</span>
                        </div>
                      </td>
                    </tr>
                  ) : filteredTrainees.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="tw-p-0">
                        <NoData message="No trainees found" />
                      </td>
                    </tr>
                  ) : (
                    filteredTrainees.map((trainee, index) => (
                      <tr
                        key={trainee.id}
                        className={"[transition:background-color_0.2s_ease] [&:hover]:tw-bg-[#f8f9fa] tw-border-b clickable-row"}
                        onClick={(e) => {
                          if (!e.target.closest('.action-buttons') && permissions.trainee_management_view_analytics) {
                            handleAnalytics(trainee.id);
                          }
                        }}
                        style={{ cursor: permissions.trainee_management_view_analytics ? 'pointer' : 'default' }}
                      >
                        <td className="tw-py-4 tw-align-middle">
                          {(pagination.page - 1) * pagination.limit + index + 1}
                        </td>
                        <td className="tw-py-4 tw-align-middle">
                          <div className="tw-flex tw-items-center">
                            {trainee.profile_pic_url ? (
                              <img
                                src={trainee.profile_pic_url}
                                alt="Profile"
                                className="tw-rounded-full tw-me-4"
                                style={{
                                  height: "40px",
                                  width: '40px',
                                  objectFit: 'cover',
                                  border: '2px solid #e9ecef'
                                }}
                              />
                            ) : (
                              <div
                                className="!tw-bg-[var(--secondary-color)] tw-rounded-full tw-me-4 tw-flex tw-items-center tw-justify-center tw-bg-slate-500 tw-text-white"
                                style={{ height: "40px", width: '40px', fontSize: '14px', fontWeight: '600' }}
                              >
                                {trainee.name ? trainee.name[0].toUpperCase() : "?"}
                              </div>
                            )}
                            <div className="tw-min-w-0">
                              <div className="![font-weight:var(--font-weight-semibold)] tw-font-semibold tw-break-words">{trainee.name}</div>
                              <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-slate-500 tw-break-all">{trainee.email}</div>
                              {trainee.mobile && <div className="tw-text-xs tw-text-slate-500">{[trainee.mobileno_code, trainee.mobile].filter(Boolean).join(' ')}</div>}
                            </div>
                          </div>
                        </td>
                        <td className="tw-py-4 tw-align-middle">
                          <div className="tw-font-medium">{trainee.active_courses || 0} active courses</div>
                          <div className="tw-font-medium">{trainee.active_classrooms || 0} active classrooms</div>
                          <div className="tw-font-medium">{trainee.certificate_count || 0} certificates</div>
                        </td>
                        <td className="tw-py-4 tw-align-middle tw-whitespace-nowrap">{formatUtcInTimeZone(trainee.createdAt, timeZone, 'MMM D, YYYY')}</td>
                        <td className="tw-py-4 tw-align-middle">
                          <span className={`tw-inline-flex tw-items-center tw-whitespace-nowrap tw-rounded-full tw-border tw-px-2.5 tw-py-1 tw-text-xs tw-font-medium tw-leading-4 tw-capitalize ${trainee.is_blocked ? 'tw-border-rose-600 tw-bg-rose-50 tw-text-rose-600' : Number(trainee.is_email_verified) === 1 ? 'tw-border-emerald-600 tw-bg-emerald-50 tw-text-emerald-600' : 'tw-border-amber-600 tw-bg-amber-50 tw-text-amber-700'}`}>
                            {trainee.is_blocked ? 'Blocked' : Number(trainee.is_email_verified) === 1 ? 'Active' : 'Pending Verification'}
                          </span>
                        </td>
                        <td className="tw-py-4 tw-flex tw-justify-start tw-items-center">
                          <div className="tw-flex tw-gap-2 tw-justify-center tw-items-center">
                            <ActionIconButton
                              icon="tabler:pencil"
                              label="Edit"
                              tone="edit"
                              title="Edit"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleShowModal('Edit', trainee);
                              }}
                              disabled={loading || !permissions.trainee_management_edit}
                            />
                            <ActionIconButton
                              icon={!trainee.is_blocked ? 'tabler:user-x' : 'tabler:user-check'}
                              label={!trainee.is_blocked ? 'Block trainee' : 'Unblock trainee'}
                              tone={!trainee.is_blocked ? 'warning' : 'success'}
                              title={!trainee.is_blocked ? 'Block' : 'Unblock'}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleStatusToggle(trainee);
                              }}
                              disabled={loading || !permissions.trainee_management_status}
                            />
                            <ActionIconButton
                              icon="tabler:trash"
                              label="Delete"
                              tone="danger"
                              title="Delete"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDeleteTraineeModal(trainee);
                              }}
                              disabled={loading || !permissions.trainee_management_delete}
                            />
                            <ActionIconButton
                              icon="tabler:chart-bar"
                              label="View Analytics"
                              tone="analytics"
                              title="View Analytics"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAnalytics(trainee.id);
                              }}
                              disabled={!permissions.trainee_management_view_analytics}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <Pagination page={pagination.page} pageSize={pagination.limit} totalRecords={pagination.totalCount} totalPages={pagination.totalPages} onPageChange={(nextPage) => setPagination(prev => ({ ...prev, page: nextPage }))} onPageSizeChange={(size) => setPagination(prev => ({ ...prev, limit: size, page: 1 }))} disabled={loading} pageSizeOptions={[5, 10, 25, 50, 100]} />

      {/* Delete trainee confirmation */}
      <Dialog show={showDeleteModal} onHide={closeDeleteTraineeModal} centered>
        <Dialog.Header closeButton className="tw-border-b">
          <Dialog.Title>Delete trainee</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body>
          <p className="tw-mb-0">
            Are you sure you want to delete{' '}
            <span className="![font-weight:var(--font-weight-semibold)] tw-font-semibold">{traineeToDelete?.name || 'this trainee'}</span>
            ? This action cannot be undone.
          </p>
        </Dialog.Body>
        <Dialog.Footer className="tw-border-t">
          <button type="button" className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-button ui-button-light"} onClick={closeDeleteTraineeModal} disabled={loading}>
            Cancel
          </button>
          <button type="button" className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-border-[transparent] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-bg-opacity:1] tw-bg-[rgb(225_29_72_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(190_18_60_/_var(--tw-bg-opacity,_1))] ui-button ui-button-danger"} onClick={confirmDeleteTrainee} disabled={loading}>
            {loading ? (
              <>
                <span className="tw-text-[#3152e8] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm tw-me-2" role="status" aria-hidden="true" />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </button>
        </Dialog.Footer>
      </Dialog>

      {/* Trainees analytics report (preview + Excel download) */}
      <Dialog
        show={showTraineesReportModal}
        onHide={handleCloseTraineesReportModal}
        size={bulkUploadStep === 'result' ? 'lg' : 'xl'}
        centered
        scrollable
      >
        <Dialog.Header closeButton className="tw-border-b">
          <Dialog.Title>Trainees analytics report</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body className={bulkUploadStep === 'preview' ? 'tw-p-0 tw-overflow-hidden max-[991.98px]:tw-overflow-y-auto' : 'tw-p-0'}>
          <p className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-slate-500 tw-text-sm tw-px-4 tw-pt-4 tw-mb-2">
            Preview matches the Excel export. Use Download to save the workbook.
          </p>
          <div className="tw-w-full tw-overflow-x-auto tw-rounded-[0.75rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [-webkit-overflow-scrolling:touch] ui-table-scroll tw-px-4 tw-pb-4" style={{ maxHeight: 'min(70vh, 560px)', overflow: 'auto' }}>
            <Table
              className={"[&_.deleted-accounts-actions_.ui-button-sm]:!tw-w-auto [&_.deleted-accounts-actions_.ui-button-sm]:!tw-h-auto [&_.deleted-accounts-actions_.ui-button-sm]:!tw-min-h-[32px] [&_.deleted-accounts-actions_.ui-button-sm]:!tw-p-[0.45rem_1.25rem] [&_.deleted-accounts-actions_.ui-button-sm]:!tw-inline-flex [&_.deleted-accounts-actions_.ui-button-sm]:!tw-items-center [&_.deleted-accounts-actions_.ui-button-sm]:!tw-justify-center [&_.deleted-accounts-actions_.btn-view]:!tw-min-w-[36px] [&_.deleted-accounts-actions_.btn-view]:!tw-p-[0.45rem_0.7rem] [&_.deleted-accounts-actions_.btn-restore]:!tw-min-w-[92px] [&_.deleted-accounts-actions_.btn-restore]:!tw-pl-[1.35rem] [&_.deleted-accounts-actions_.btn-restore]:!tw-pr-[1.35rem] [&_.deleted-accounts-actions_.btn-delete]:!tw-min-w-[92px] [&_.deleted-accounts-actions_.btn-delete]:!tw-pl-[1.35rem] [&_.deleted-accounts-actions_.btn-delete]:!tw-pr-[1.35rem] tw-w-full tw-border-collapse tw-text-left tw-text-[0.875rem] tw-leading-[1.25rem] [&_>_thead]:[--tw-bg-opacity:1] [&_>_thead]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&_th]:tw-whitespace-nowrap [&_th]:tw-border-b-[1px] [&_th]:tw-text-[0.75rem] [&_th]:tw-leading-[1rem] [&_th]:tw-font-semibold [&_th]:[--tw-text-opacity:1] [&_th]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] [&_td]:tw-border-b-[1px] [&_td]:tw-align-middle [&_td]:[--tw-text-opacity:1] [&_td]:tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&_tbody_tr:last-child_td]:tw-border-b-[0px] [&_tbody_tr]:[transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [&_tbody_tr]:[transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [&_tbody_tr]:[transition-duration:150ms] [&_tbody_tr:hover]:[--tw-bg-opacity:1] [&_tbody_tr:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&_th]:[border-inline-end-width:1px] [&_th]:[--tw-border-opacity:1] [&_th]:tw-border-[rgb(241_245_249_/_var(--tw-border-opacity,_1))] [&_td]:[border-inline-end-width:1px] [&_td]:[--tw-border-opacity:1] [&_td]:tw-border-[rgb(241_245_249_/_var(--tw-border-opacity,_1))] [&_th]:tw-pl-3 [&_th]:tw-pr-3 [&_th]:tw-pt-2 [&_th]:tw-pb-2 [&_td]:tw-pl-3 [&_td]:tw-pr-3 [&_td]:tw-pt-2 [&_td]:tw-pb-2 ui-table ui-table-sm ui-table-bordered tw-align-middle tw-mb-0"}
              style={{ fontSize: '0.875rem', width: 'max-content', minWidth: '100%' }}
            >
              <TableHeader className="[--tw-bg-opacity:1] tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-table-light" style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr>
                  {TRAINEES_ANALYTICS_REPORT_COLUMNS.map((col) => (
                    <th key={col.key} className="tw-whitespace-nowrap">
                      {col.label}
                    </th>
                  ))}
                </tr>
              </TableHeader>
              <TableBody>
                {traineesReportRows.length === 0 ? (
                  <tr>
                    <td colSpan={TRAINEES_ANALYTICS_REPORT_COLUMNS.length} className="tw-p-0">
                      <NoData message="No data available" />
                    </td>
                  </tr>
                ) : (
                  traineesReportRows.map((row, rowIndex) => (
                    <tr key={row.trainee_id}>
                      {TRAINEES_ANALYTICS_REPORT_COLUMNS.map((col) => (
                        <td key={col.key} className="tw-whitespace-nowrap">
                          {col.key === '_displayRowNo' ? rowIndex + 1 : (row[col.key] ?? '—')}
                        </td>
                      ))}
                    </tr>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Dialog.Body>
        <Dialog.Footer className="tw-border-t">
          <button type="button" className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-button ui-button-light"} onClick={handleCloseTraineesReportModal}>
            Close
          </button>
          <button
            type="button"
            className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary"}
            onClick={handleTraineesReportDownloadExcel}
            disabled={!traineesReportRows.length}
          >
            <Icon icon="fluent:arrow-download-24-regular" width="18" height="18" className="tw-me-1" />
            Download Excel
          </button>
        </Dialog.Footer>
      </Dialog>

      {/* Add/Edit Trainee Modal */}
      <Dialog show={showModal} onHide={handleCloseModal} size="lg">
        <Dialog.Header closeButton className="tw-sticky tw-top-0 tw-z-[20] tw-border-b ui-sticky tw-bg-white">
          <Dialog.Title>{mode === 'Create' ? 'Add Trainee' : 'Edit Trainee'}</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body className="tw-p-0">
          <div style={{ height: 'calc(100vh - 200px)', overflowY: 'auto' }}>
            <form className="tw-p-6" ref={traineeFormRef}>
              {/* Profile Image Upload */}
              <div className="tw-mb-6 tw-text-center">
                <div className="tw-relative tw-inline-block">
                  <div
                    className="tw-rounded-full tw-overflow-hidden"
                    style={{
                      width: '150px',
                      height: '150px',
                      border: '2px dashed #ccc',
                      backgroundColor: '#f8f9fa',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {imagePreview ? (
                      <img
                        src={imagePreview}
                        alt="Profile Preview"
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover'
                        }}
                      />
                    ) : (
                      <div className="tw-text-center tw-p-6">
                        <Icon
                          icon="fluent:person-24-regular"
                          width="40"
                          height="40"
                          className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-2"
                        />
                        <div className="!tw-text-[var(--secondary-color)] tw-text-slate-500" style={{ fontSize: '0.875rem' }}>
                          Upload Photo
                        </div>
                      </div>
                    )}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    className="tw-absolute tw-top-0 tw-start-0 tw-opacity-0"
                    style={{
                      width: '150px',
                      height: '150px',
                      cursor: 'pointer'
                    }}
                    onChange={handleImageChange}
                  />
                  {imagePreview && (
                    <button
                      type="button"
                      className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-border-[transparent] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-bg-opacity:1] tw-bg-[rgb(225_29_72_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(190_18_60_/_var(--tw-bg-opacity,_1))] ui-button ui-button-danger tw-absolute"}
                      onClick={() => {
                        setImagePreview(null);
                        setFormData(prev => ({
                          ...prev,
                          profileImage: null
                        }));
                      }}
                      style={{
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        padding: '0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        top: '5px',
                        right: '5px',
                        border: '2px solid #fff',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
                      }}
                    >
                      <Icon icon="fluent:dismiss-12-filled" width="12" height="12" color="#fff" />
                    </button>
                  )}
                </div>
                <div className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mt-2" style={{ fontSize: '0.875rem' }}>
                  Click to upload profile photo
                </div>
              </div>

              <div className="tw-mb-6">
                <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">
                  Name<span className="!tw-text-[var(--danger-color)] tw-text-rose-600">*</span>
                </label>
                <input
                  type="text"
                  className={`[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input tw-bg-slate-50   ${errors.name ? "!tw-border-[#dc3545]  [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}`}
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  maxLength={25}
                  required
                />
                {errors.name && <div className="tw-block tw-w-full tw-mt-1 tw-text-[0.75rem] tw-text-[rgb(146_64_14_/_var(--tw-text-opacity,_1))] tw-leading-[1rem] [--tw-text-opacity:1] ui-field-feedback">{errors.name}</div>}
                <div className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mt-1" style={{ fontSize: '0.875rem' }}>
                  Maximum 25 characters
                </div>
              </div>

              <div className="tw-mb-6">
                <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">
                  Email<span className="!tw-text-[var(--danger-color)] tw-text-rose-600">*</span>
                </label>
                <input
                  type="email"
                  className={`[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input tw-bg-slate-50   ${errors.email ? "!tw-border-[#dc3545]  [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}      ${mode === "Edit" ? "cursor-not-allowed" : ""}`}
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="lms.admin@gmail.com"
                  required
                  readOnly={mode === 'Edit'}
                  style={mode === 'Edit' ? { backgroundColor: '#e9ecef' } : {}}
                />
                {errors.email && <div role="alert" className="tw-mt-1 tw-text-xs tw-leading-4 tw-text-rose-600">{errors.email}</div>}
                {mode === 'Edit' && (
                  <div className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mt-1" style={{ fontSize: '0.875rem' }}>
                    Email cannot be changed after creation
                  </div>
                )}
              </div>

              <div className="tw-mb-6">
                <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">
                  Gender<span className="!tw-text-[var(--danger-color)] tw-text-rose-600">*</span>
                </label>
                <select
                  className={`[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [appearance:none] tw-pe-[2.25rem] tw-bg-[url(data:image/svg+xml;base64,PHN2ZyB4bWxucz0naHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmcnIHdpZHRoPScyMCcgaGVpZ2h0PScyMCcgZmlsbD0nbm9uZScgc3Ryb2tlPScjNjQ3NDhiJyB2aWV3Qm94PScwIDAgMjQgMjQnPjxwYXRoIGQ9J203IDEwIDUgNSA1LTUnLz48L3N2Zz4=)] tw-bg-no-repeat [&[multiple]]:tw-bg-[none] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-select tw-bg-slate-50   ${errors.gender ? "!tw-border-[#dc3545]  [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}`}
                  name="gender"
                  value={formData.gender}
                  onChange={handleInputChange}
                  required
                >
                  <option value="">Select Gender</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
                {errors.gender && <div className="tw-block tw-w-full tw-mt-1 tw-text-[0.75rem] tw-text-[rgb(146_64_14_/_var(--tw-text-opacity,_1))] tw-leading-[1rem] [--tw-text-opacity:1] ui-field-feedback">{errors.gender}</div>}
              </div>

              <div className="tw-mb-6">
                <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">
                  Date of Birth<span className="!tw-text-[var(--danger-color)] tw-text-rose-600">*</span>
                </label>
                <SingleDatePicker
                  className={`[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input tw-bg-slate-50   ${errors.dateOfBirth ? "!tw-border-[#dc3545]  [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}`}
                  name="dateOfBirth"
                  value={formData.dateOfBirth}
                  onChange={handleInputChange}
                  maxDate={new Date()}
                  placeholder="dd-mm-yyyy"
                />
                {errors.dateOfBirth && <div className="tw-block tw-w-full tw-mt-1 tw-text-[0.75rem] tw-text-[rgb(146_64_14_/_var(--tw-text-opacity,_1))] tw-leading-[1rem] [--tw-text-opacity:1] ui-field-feedback">{errors.dateOfBirth}</div>}
              </div>

              <div className="tw-mb-6">
                <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">Phone Number</label>
                <div className={"tw-flex tw-flex-wrap tw-mx-[calc(var(--grid-x)_/_-2)] tw-mt-[calc(-1_*_var(--grid-y))] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] [--grid-x:.5rem] [--grid-y:.5rem] ui-row tw-gap-grid-2"}>
                  <div className="tw-min-w-[0px] ui-column md:tw-w-1/3">
                    <CountryCodeDropdown
                      value={formData.countryCode}
                      onChange={(country) => {
                        const nextRule = getPhoneRuleFromCountry(country);
                        setFormData((prev) => ({
                          ...prev,
                          countryCode: country.code,
                          phoneNumber: (prev.phoneNumber || '').replace(/\D/g, '').slice(0, nextRule.max),
                        }));
                        setErrors((er) => ({ ...er, phoneNumber: '', countryCode: '' }));
                      }}
                    />
                  </div>
                  <div className="tw-min-w-[0px] ui-column md:tw-w-2/3">
                    <input
                      type="text"
                      className={`[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input tw-bg-slate-50   ${errors.phoneNumber ? "!tw-border-[#dc3545]  [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}`}
                      name="phoneNumber"
                      value={formData.phoneNumber}
                      onChange={handleInputChange}
                      placeholder={phoneLengthPlaceholder(phoneRule)}
                      maxLength={phoneRule.max}
                    />
                    {errors.phoneNumber && (
                      <div className="tw-w-full tw-mt-1 tw-text-[0.75rem] tw-text-[rgb(146_64_14_/_var(--tw-text-opacity,_1))] tw-leading-[1rem] [--tw-text-opacity:1] ui-field-feedback tw-block">{errors.phoneNumber}</div>
                    )}
                  </div>
                </div>
                {!errors.phoneNumber && (
                  <div className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mt-1" style={{ fontSize: '0.875rem' }}>
                    {`${formData.countryCode}: ${phoneLengthHintSuffix(phoneRule)}`}
                  </div>
                )}
              </div>

              <div className="tw-mb-6">
                <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">Employee No</label>
                <input
                  type="text"
                  className={`[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input tw-bg-slate-50   ${errors.employeeNo ? "!tw-border-[#dc3545]  [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}`}
                  name="employeeNo"
                  value={formData.employeeNo}
                  onChange={handleInputChange}
                  maxLength={50}
                />
                {errors.employeeNo && <div className="tw-block tw-w-full tw-mt-1 tw-text-[0.75rem] tw-text-[rgb(146_64_14_/_var(--tw-text-opacity,_1))] tw-leading-[1rem] [--tw-text-opacity:1] ui-field-feedback">{errors.employeeNo}</div>}
                <div className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mt-1" style={{ fontSize: '0.875rem' }}>
                  Maximum 50 characters
                </div>
              </div>

              <div className="tw-mb-6">
                <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">Company Code</label>
                <input
                  type="text"
                  className={`[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input tw-bg-slate-50   ${errors.companyCode ? "!tw-border-[#dc3545]  [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}`}
                  name="companyCode"
                  value={formData.companyCode}
                  onChange={handleInputChange}
                  maxLength={50}
                />
                {errors.companyCode && <div className="tw-block tw-w-full tw-mt-1 tw-text-[0.75rem] tw-text-[rgb(146_64_14_/_var(--tw-text-opacity,_1))] tw-leading-[1rem] [--tw-text-opacity:1] ui-field-feedback">{errors.companyCode}</div>}
                <div className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mt-1" style={{ fontSize: '0.875rem' }}>
                  Maximum 50 characters
                </div>
              </div>

              <div className="tw-mb-6">
                <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">Company Description</label>
                <textarea
                  className={`[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input tw-bg-slate-50   ${errors.companyDescription ? "!tw-border-[#dc3545]  [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}`}
                  name="companyDescription"
                  value={formData.companyDescription}
                  onChange={handleInputChange}
                  maxLength={255}
                  rows={3}
                />
                {errors.companyDescription && <div className="tw-block tw-w-full tw-mt-1 tw-text-[0.75rem] tw-text-[rgb(146_64_14_/_var(--tw-text-opacity,_1))] tw-leading-[1rem] [--tw-text-opacity:1] ui-field-feedback">{errors.companyDescription}</div>}
                <div className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mt-1" style={{ fontSize: '0.875rem' }}>
                  Maximum 255 characters
                </div>
              </div>

              <div className="tw-mb-6">
                <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">Job Title</label>
                <input
                  type="text"
                  className={`[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input tw-bg-slate-50   ${errors.jobTitle ? "!tw-border-[#dc3545]  [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}`}
                  name="jobTitle"
                  value={formData.jobTitle}
                  onChange={handleInputChange}
                  maxLength={1000}
                />
                {errors.jobTitle && <div className="tw-block tw-w-full tw-mt-1 tw-text-[0.75rem] tw-text-[rgb(146_64_14_/_var(--tw-text-opacity,_1))] tw-leading-[1rem] [--tw-text-opacity:1] ui-field-feedback">{errors.jobTitle}</div>}
                <div className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mt-1" style={{ fontSize: '0.875rem' }}>
                  Maximum 1000 characters
                </div>
              </div>

              <div className="tw-mb-6">
                <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">Date Joined</label>
                <SingleDatePicker
                  className={`[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input tw-bg-slate-50   ${errors.dateJoined ? "!tw-border-[#dc3545]  [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}`}
                  name="dateJoined"
                  value={formData.dateJoined}
                  onChange={handleInputChange}
                  maxDate={new Date()}
                  placeholder="dd-mm-yyyy"
                />
                {errors.dateJoined && <div className="tw-block tw-w-full tw-mt-1 tw-text-[0.75rem] tw-text-[rgb(146_64_14_/_var(--tw-text-opacity,_1))] tw-leading-[1rem] [--tw-text-opacity:1] ui-field-feedback">{errors.dateJoined}</div>}
                <div className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mt-1" style={{ fontSize: '0.875rem' }}>
                  Format: dd-mm-yyyy
                </div>
              </div>

              <div className="tw-mb-6">
                <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">IC No</label>
                <input
                  type="text"
                  className={`[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input tw-bg-slate-50   ${errors.icNo ? "!tw-border-[#dc3545]  [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}`}
                  name="icNo"
                  value={formData.icNo}
                  onChange={handleInputChange}
                  maxLength={255}
                />
                {errors.icNo && <div className="tw-block tw-w-full tw-mt-1 tw-text-[0.75rem] tw-text-[rgb(146_64_14_/_var(--tw-text-opacity,_1))] tw-leading-[1rem] [--tw-text-opacity:1] ui-field-feedback">{errors.icNo}</div>}
                <div className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mt-1" style={{ fontSize: '0.875rem' }}>
                  Maximum 255 characters
                </div>
              </div>

              <div className="tw-mb-6">
                <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">FIN No</label>
                <input
                  type="text"
                  className={`[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input tw-bg-slate-50   ${errors.finNo ? "!tw-border-[#dc3545]  [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}`}
                  name="finNo"
                  value={formData.finNo}
                  onChange={handleInputChange}
                  maxLength={255}
                />
                {errors.finNo && <div className="tw-block tw-w-full tw-mt-1 tw-text-[0.75rem] tw-text-[rgb(146_64_14_/_var(--tw-text-opacity,_1))] tw-leading-[1rem] [--tw-text-opacity:1] ui-field-feedback">{errors.finNo}</div>}
                <div className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mt-1" style={{ fontSize: '0.875rem' }}>
                  Maximum 255 characters
                </div>
              </div>

              <div className="tw-mb-6">
                <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">PWM Rank</label>
                <input
                  type="text"
                  className={`[&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input tw-bg-slate-50   ${errors.pwmRank ? "!tw-border-[#dc3545]  [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}`}
                  name="pwmRank"
                  value={formData.pwmRank}
                  onChange={handleInputChange}
                  maxLength={1000}
                />
                {errors.pwmRank && <div className="tw-block tw-w-full tw-mt-1 tw-text-[0.75rem] tw-text-[rgb(146_64_14_/_var(--tw-text-opacity,_1))] tw-leading-[1rem] [--tw-text-opacity:1] ui-field-feedback">{errors.pwmRank}</div>}
                <div className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mt-1" style={{ fontSize: '0.875rem' }}>
                  Maximum 1000 characters
                </div>
              </div>

              <div className="tw-mb-6">
                <label className="tw-mb-2 tw-block tw-text-[0.875rem] tw-leading-[1.25rem] tw-font-medium [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] ui-label">
                  Password{mode === 'Create' && <span className="!tw-text-[var(--danger-color)] tw-text-rose-600">*</span>}
                  {mode === 'Edit' && (
                    <span className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-font-normal"> (optional — leave blank to keep current password)</span>
                  )}
                </label>
                <div className="tw-flex tw-items-center tw-relative trainee-password-input-shell">
                  <input
                    type={showPassword ? "text" : "password"}
                    className={`!tw-pr-[3.25rem] [&.is-invalid]:!tw-bg-[none] [&.is-invalid]:!tw-pr-[3.25rem] [&::placeholder]:[--tw-text-opacity:1] [&::placeholder]:tw-text-[rgb(148_163_184_/_var(--tw-text-opacity,_1))] [&:focus]:[--tw-border-opacity:1] [&:focus]:tw-border-[rgb(82_101_232_/_var(--tw-border-opacity,_1))] [&:focus]:[outline:2px_solid_transparent] [&:focus]:[outline-offset:2px] [&:focus]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus]:[--tw-ring-opacity:1] [&:focus]:[--tw-ring-color:rgb(224_231_255_/_var(--tw-ring-opacity,_1))] [&:disabled]:[--tw-bg-opacity:1] [&:disabled]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] [&:disabled]:[--tw-text-opacity:1] [&:disabled]:tw-text-[rgb(100_116_139_/_var(--tw-text-opacity,_1))] !tw-bg-[#F9F9F9] tw-block tw-w-full tw-rounded-[0.5rem] tw-border-[1px] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-pl-3 tw-pr-3 tw-pt-2.5 tw-pb-2.5 [--tw-text-opacity:1] tw-text-[rgb(15_23_42_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] tw-min-h-[42px] [&[type='file']]:tw-text-[0.875rem] [&[type='file']]:tw-leading-[1.25rem] [&[type='file']::file-selector-button]:tw-me-3 [&[type='file']::file-selector-button]:tw-rounded-[0.375rem] [&[type='file']::file-selector-button]:tw-border-[0px] [&[type='file']::file-selector-button]:[--tw-bg-opacity:1] [&[type='file']::file-selector-button]:tw-bg-[rgb(238_242_255_/_var(--tw-bg-opacity,_1))] [&[type='file']::file-selector-button]:tw-pl-3 [&[type='file']::file-selector-button]:tw-pr-3 [&[type='file']::file-selector-button]:tw-pt-1 [&[type='file']::file-selector-button]:tw-pb-1 [&[type='file']::file-selector-button]:[--tw-text-opacity:1] [&[type='file']::file-selector-button]:tw-text-[rgb(53_71_186_/_var(--tw-text-opacity,_1))] [&.is-invalid]:[--tw-border-opacity:1] [&.is-invalid]:tw-border-[rgb(251_191_36_/_var(--tw-border-opacity,_1))] [&.is-invalid]:[--tw-ring-opacity:1] [&.is-invalid]:[--tw-ring-color:rgb(254_243_199_/_var(--tw-ring-opacity,_1))] ui-input tw-bg-slate-50 tw-pe-12 trainee-password-input   ${errors.password ? "!tw-border-[#dc3545]  [&:focus]:!tw-border-[#dc3545] [&:focus]:!tw-shadow-[0_0_0_0.25rem_rgba(220,_53,_69,_0.25)] is-invalid" : ""}`}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required={mode === 'Create'}
                    minLength={mode === 'Create' ? 8 : undefined}
                    autoComplete="new-password"
                    placeholder={
                      mode === 'Edit'
                        ? 'Leave blank to keep current password'
                        : 'Password'
                    }
                  />
                  <button
                    type="button"
                    className={"tw-absolute tw-right-[0.875rem] tw-top-[50%] [transform:translateY(-50%)] tw-w-6 tw-h-6 tw-flex tw-items-center tw-justify-center tw-z-[3] tw-text-[#6b7280] tw-no-underline !tw-bg-[transparent] tw-border-0 tw-p-0 !tw-shadow-[none] tw-pointer-events-auto tw-cursor-pointer [&:hover]:tw-text-[#4b5563] [&:focus]:tw-text-[#4b5563] trainee-password-toggle"}
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    <Icon
                      icon={showPassword ? "fluent:eye-off-24-regular" : "fluent:eye-24-regular"}
                      className="!tw-text-[var(--secondary-color)] tw-text-slate-500"
                      width="20"
                      height="20"
                    />
                  </button>
                  {errors.password && <div className="tw-w-full tw-mt-1 tw-text-[0.75rem] tw-text-[rgb(146_64_14_/_var(--tw-text-opacity,_1))] tw-leading-[1rem] [--tw-text-opacity:1] ui-field-feedback tw-block">{errors.password}</div>}
                </div>
                <div className="!tw-text-[var(--secondary-color)] tw-block tw-text-[0.75rem] tw-leading-[1rem] [--tw-text-opacity:1] ui-help tw-text-slate-500 tw-mt-2">
                  <div className="![font-weight:var(--font-weight-medium)] !tw-text-[var(--secondary-color)] tw-font-medium tw-text-slate-500">Password Requirements:</div>
                  <ul className="tw-mb-0 tw-ps-4 tw-mt-1 tw-list-none tw-pl-0">
                    <li className={traineePasswordRequirementState.length ? "!tw-text-[var(--success-color)] tw-text-emerald-600" : ""}>
                      <Icon
                        icon={traineePasswordRequirementState.length ? 'mdi:check-circle' : 'mdi:circle-outline'}
                        className="tw-me-1"
                        width="16"
                      />
                      At least 8 characters
                    </li>
                    <li className={traineePasswordRequirementState.letter ? "!tw-text-[var(--success-color)] tw-text-emerald-600" : ""}>
                      <Icon
                        icon={traineePasswordRequirementState.letter ? 'mdi:check-circle' : 'mdi:circle-outline'}
                        className="tw-me-1"
                        width="16"
                      />
                      At least one letter
                    </li>
                    <li className={traineePasswordRequirementState.number ? "!tw-text-[var(--success-color)] tw-text-emerald-600" : ""}>
                      <Icon
                        icon={traineePasswordRequirementState.number ? 'mdi:check-circle' : 'mdi:circle-outline'}
                        className="tw-me-1"
                        width="16"
                      />
                      At least one number
                    </li>
                    <li className={traineePasswordRequirementState.special ? "!tw-text-[var(--success-color)] tw-text-emerald-600" : ""}>
                      <Icon
                        icon={traineePasswordRequirementState.special ? 'mdi:check-circle' : 'mdi:circle-outline'}
                        className="tw-me-1"
                        width="16"
                      />
                      At least one allowed special character
                    </li>
                  </ul>

                </div>
              </div>
            </form>
          </div>
          {/* Buttons */}
          <div className="tw-p-6 tw-border-t tw-bg-white" style={{ position: 'sticky', bottom: 0 }}>
            <button
              type="button"
              className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-button ui-button-light tw-w-full tw-mb-4"}
              onClick={handleCloseModal}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary tw-w-full"}
              onClick={handleSave}
              disabled={loading}
            >
              {loading ? (
                <>
                  <span className="tw-text-[#3152e8] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm tw-me-2" role="status" aria-hidden="true"></span>
                  {mode === 'Create' ? 'Creating...' : 'Updating...'}
                </>
              ) : (
                mode === 'Create' ? 'Add Trainee' : 'Update Trainee'
              )}
            </button>
          </div>
        </Dialog.Body>
      </Dialog>

      {/* Bulk Upload Modal */}
      <Dialog
        show={showBulkUploadModal}
        onHide={handleCloseBulkUploadModal}
        size="xl"
        centered
        dialogClassName="bulk-upload-modal-dialog"
        contentClassName="bulk-upload-modal-content"
      >
        <Dialog.Header closeButton className="tw-border-b">
          <Dialog.Title>Bulk Upload Trainees</Dialog.Title>
        </Dialog.Header>
        <Dialog.Body className="tw-p-0">
          <input
            ref={bulkFileInputRef}
            type="file"
            accept=".xlsx,.xls,.csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
            onChange={handleBulkFileSelect}
            style={{ display: 'none' }}
          />
          {bulkUploadStep === 'upload' && (
            <div className="tw-p-5 sm:tw-p-6">
              <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-4">
                <div>
                  <div className="tw-text-xs tw-font-semibold tw-uppercase tw-tracking-wide tw-text-blue-600">Step 1 of 3 · Upload</div>
                  <h5 className="tw-mb-1 tw-mt-1 tw-text-xl tw-font-semibold">Add trainees from a file</h5>
                  <p className="tw-mb-0 tw-text-sm tw-text-slate-600">Choose a CSV or Excel file. You can correct its rows in the next step.</p>
                </div>
                <div className="tw-flex tw-flex-wrap tw-gap-2">
                  <button type="button" className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-blue-200 tw-bg-white tw-px-4 tw-py-2.5 tw-font-medium tw-text-blue-700 hover:tw-bg-blue-50" onClick={handleDownloadTemplate}>
                    <Icon icon="fluent:arrow-download-24-regular" width="18" height="18" />
                    Download template
                  </button>
                  <button type="button" className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-blue-600 tw-bg-blue-600 tw-px-4 tw-py-2.5 tw-font-medium tw-text-white hover:tw-bg-blue-700 disabled:tw-opacity-50" onClick={() => bulkFileInputRef.current?.click()} disabled={bulkUploadLoading}>
                    <Icon icon="fluent:arrow-upload-24-regular" width="18" height="18" />
                    Select file
                  </button>
                </div>
              </div>
              <div className="tw-mt-5 tw-grid tw-gap-4 lg:tw-grid-cols-2">
                <section className="tw-rounded-xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-4">
                  <h6 className="tw-mb-3 tw-font-semibold tw-text-slate-900">Required fields</h6>
                  <div className="tw-grid tw-gap-x-5 tw-gap-y-2 sm:tw-grid-cols-2 tw-text-sm">
                    <div><strong>Name</strong><span className="tw-block tw-text-slate-500">Up to 25 characters</span></div>
                    <div><strong>Email</strong><span className="tw-block tw-text-slate-500">Valid email; matching key</span></div>
                    <div><strong>Gender</strong><span className="tw-block tw-text-slate-500">Male, Female, or Other</span></div>
                    <div><strong>Date of Birth</strong><span className="tw-block tw-text-slate-500">dd-mm-yyyy</span></div>
                    <div><strong>Password</strong><span className="tw-block tw-text-slate-500">8+ characters, letter, number, special character</span></div>
                  </div>
                </section>
                <section className="tw-rounded-xl tw-border tw-border-blue-100 tw-bg-blue-50 tw-p-4 tw-text-sm">
                  <h6 className="tw-mb-2 tw-font-semibold tw-text-slate-900">Optional fields</h6>
                  <p className="tw-mb-3 tw-text-slate-700">Country Code, Phone Number, Employee No, Company Code, Company Description, Job Title, Date Joined, IC No, FIN No, and PWM Rank.</p>
                  <div className="tw-border-t tw-border-blue-100 tw-pt-3">
                    <h6 className="tw-mb-2 tw-font-semibold tw-text-slate-900">Before you import</h6>
                    <ul className="tw-mb-0 tw-space-y-1 tw-pl-5 tw-text-slate-700">
                      <li>Use .csv, .xlsx, or .xls. Headers must match the template; column order does not matter.</li>
                      <li>Missing fields can be fixed in the preview. Extra columns are ignored.</li>
                      <li>Matching emails update existing accounts, including passwords. Changing an email may target a different account.</li>
                    </ul>
                  </div>
                </section>
              </div>
            </div>
          )}
          {bulkUploadStep === 'preview' && (
            <div className="tw-h-[min(70dvh,_650px)] tw-min-h-[420px] max-[991.98px]:tw-h-auto max-[991.98px]:tw-min-h-[auto] tw-flex tw-flex-col bulk-upload-preview-shell">
              <div className="tw-border-b tw-px-5 tw-py-3 tw-bg-white">
                <div className="tw-flex tw-flex-wrap tw-justify-between tw-items-start tw-gap-4">
                  <div>
                    <div className="tw-flex tw-items-center tw-gap-2">
                      <Icon icon="fluent:checkmark-circle-24-regular" width="22" height="22" className="!tw-text-[var(--success-color)] tw-text-emerald-600" />
                      <div>
                        <div className="![font-weight:var(--font-weight-semibold)] tw-font-semibold">File Loaded: {bulkUploadFile?.name}</div>
                        <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-slate-500 tw-text-sm">
                          Edit any cell below, then verify the latest rows before importing. Changes here do not alter your source file.
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="tw-flex tw-flex-wrap tw-gap-2">
                    <button
                      type="button"
                      className="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-lg tw-border tw-border-slate-200 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-slate-700 hover:tw-bg-slate-50 disabled:tw-opacity-50"
                      onClick={() => {
                        resetBulkUploadState();
                        bulkFileInputRef.current?.click();
                      }}
                      disabled={bulkUploadLoading}
                    >
                      <Icon icon="fluent:arrow-sync-24-regular" width="16" height="16" />
                      Change file
                    </button>
                  </div>
                </div>

                {bulkUploadServerError && (
                  <div role="alert" className="tw-mt-4 tw-rounded-xl tw-border tw-border-rose-200 tw-bg-rose-50 tw-px-4 tw-py-3 tw-text-sm tw-text-rose-800">{bulkUploadServerError}</div>
                )}

                <div className={"tw-flex tw-flex-wrap tw-mx-[calc(var(--grid-x)_/_-2)] [:where(&)_>_*]:tw-shrink-[0] [:where(&)_>_*]:tw-w-full [:where(&)_>_*]:tw-max-w-full [:where(&)_>_*]:tw-px-[calc(var(--grid-x)_/_2)] [:where(&)_>_*]:tw-mt-[var(--grid-y)] [--grid-x:.5rem] [--grid-y:.5rem] ui-row tw-gap-grid-2 tw-mt-3"}>
                  {[
                    { key: BULK_PREVIEW_FILTERS.total, label: 'Total rows', value: bulkUploadSummary.total, tone: '' },
                    { key: BULK_PREVIEW_FILTERS.valid, label: 'New trainees', value: bulkHasCurrentServerCheck ? bulkUploadSummary.validCount : '—', tone: 'tw-text-emerald-600' },
                    { key: BULK_PREVIEW_FILTERS.existing, label: 'Existing to update', value: bulkHasCurrentServerCheck ? bulkUploadSummary.existingCount : '—', tone: 'tw-text-brand-600' },
                    { key: BULK_PREVIEW_FILTERS.issues, label: 'Rows with issues', value: bulkUploadSummary.invalidCount, tone: 'tw-text-rose-600' }
                  ].map((card) => {
                    const isActive = bulkPreviewFilter === card.key;
                    return (
                      <div className="tw-min-w-[0px] ui-column tw-w-full md:tw-w-1/4" key={card.key}>
                        <button
                          type="button"
                          disabled={!bulkHasCurrentServerCheck && (card.key === BULK_PREVIEW_FILTERS.valid || card.key === BULK_PREVIEW_FILTERS.existing)}
                          className={`[&.tw-border-black:hover]:tw-bg-[rgba(0,_0,_0,_0.05)] [[data-theme='dark']_&.tw-border-black]:!tw-border-[var(--border-color)] [[data-theme='dark']_&.tw-border-black]:!tw-text-[var(--text-primary)] [[data-theme='dark']_&.tw-border-black:hover]:tw-bg-[var(--bg-tertiary)] tw-bg-[#f8f9fa] [&.is-active]:tw-bg-[#ffffff] [&.is-active]:!tw-border-[#0d6efd] tw-border tw-rounded-xl tw-p-4 tw-h-full tw-w-full tw-text-left disabled:tw-cursor-not-allowed disabled:tw-opacity-60 bulk-upload-summary-card   ${isActive ? "is-active" : "tw-bg-slate-50"}`}
                          onClick={() => handleBulkPreviewFilterChange(card.key)}
                        >
                          <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-slate-500 tw-text-sm">{card.label}</div>
                          <div className={`![font-weight:var(--font-weight-semibold)] tw-text-xl tw-font-semibold   ${card.tone}`}>{card.value || 0}</div>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="tw-overflow-hidden max-[991.98px]:tw-flex-col tw-flex tw-grow bulk-upload-preview-body" style={{ minHeight: 0 }}>
                <aside
                  className="tw-min-h-[0] max-[991.98px]:!tw-w-full max-[991.98px]:!tw-max-w-full max-[991.98px]:!tw-min-w-full max-[991.98px]:tw-max-h-[240px] max-[991.98px]:!tw-border-r-0 max-[991.98px]:tw-border-b-[1px] max-[991.98px]:tw-border-solid max-[991.98px]:tw-border-b-[#dee2e6] tw-border-e tw-bg-white tw-flex tw-flex-col bulk-upload-issues-panel"
                  style={{
                    width: '250px',
                    maxWidth: '250px',
                    minWidth: '230px'
                  }}
                >
                  <div className="tw-p-4 tw-border-b">
                    <div className="![font-weight:var(--font-weight-semibold)] tw-font-semibold tw-mb-1">Rows</div>
                    <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-slate-500 tw-text-sm">
                      Select a row to view and edit all its fields.
                    </div>
                  </div>
                  <div className="tw-overflow-y-auto tw-min-h-[0] tw-p-4 tw-grow bulk-upload-issues-scroll">
                    {bulkFilteredRows.length > 0 ? (
                      <div className="tw-flex tw-flex-col tw-gap-2">
                        {bulkFilteredRows.map((row) => {
                          const isActive = bulkSelectedRow?.rowNumber === row.rowNumber;
                          const status = row.hasIssues ? 'issue' : bulkHasCurrentServerCheck ? getBulkPreviewRowStatus(row) : 'pending';
                          const statusLabel = status === 'issue' ? 'Issue' : status === 'existing' ? 'Update' : status === 'pending' ? 'Check' : 'New';
                          const statusClass = status === 'issue'
                            ? 'tw-bg-rose-50   tw-text-rose-600'
                            : status === 'existing'
                              ? 'tw-bg-brand-50   tw-text-brand-600'
                              : status === 'pending'
                                ? 'tw-bg-amber-50 tw-text-amber-700'
                              : 'tw-bg-emerald-50   tw-text-emerald-600';
                          return (
                            <button
                              key={row.id}
                              type="button"
                              className={`tw-flex tw-items-center tw-justify-between tw-gap-[12px] tw-w-full tw-p-[10px_12px] tw-border-[1px] tw-border-solid tw-border-[#dee2e6] tw-rounded-[8px] tw-bg-[#ffffff] tw-text-[#212529] tw-text-left [&.is-active]:tw-border-[#0d6efd] [&.is-active]:tw-bg-[#e7f1ff] [&_.ui-badge]:tw-shrink-[0] bulk-upload-row-item   ${isActive ? "is-active" : ""}`}
                              onClick={() => handleBulkPreviewRowClick(row.rowNumber)}
                            >
                              <span className="tw-min-w-0 tw-text-left">
                                <span className="tw-block tw-font-semibold tw-text-[0.875rem] bulk-upload-row-item-label">Row {row.rowNumber}</span>
                                <span className="tw-block tw-truncate tw-text-xs tw-text-slate-500" title={row.values.name || row.values.email}>{row.values.name || row.values.email || 'Unnamed trainee'}</span>
                              </span>
                              <span className={`[&.tw-bg-brand-50]:!tw-bg-[#e7f1ff] [&.tw-bg-brand-50]:tw-border-[1px] [&.tw-bg-brand-50]:tw-border-solid [&.tw-bg-brand-50]:tw-border-[#b6d4fe] tw-inline-flex tw-items-center tw-gap-1 tw-rounded-full tw-pl-2.5 tw-pr-2.5 tw-pt-1 tw-pb-1 tw-text-[0.75rem] tw-font-medium tw-leading-[1rem] ui-badge   ${statusClass}`}>{statusLabel}</span>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="tw-rounded-[0.75rem] tw-border-[1px] tw-p-4 tw-text-[0.875rem] tw-leading-[1.25rem] [--tw-border-opacity:1] tw-border-[rgb(209_250_229_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(236_253_245_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(6_78_59_/_var(--tw-text-opacity,_1))] ui-alert ui-alert-success tw-mb-0">
                        {bulkUploadRows.length === 0
                          ? 'No preview rows available.'
                          : 'No rows in this view.'}
                      </div>
                    )}
                  </div>
                </aside>

                <section className="tw-min-h-[0] tw-grow tw-flex tw-flex-col bulk-upload-table-panel" style={{ minWidth: 0, minHeight: 0 }}>
                  <div className="tw-p-4 tw-border-b tw-bg-white">
                    <div className="tw-flex tw-flex-wrap tw-justify-between tw-items-center tw-gap-2">
                      <div>
                        <div className="![font-weight:var(--font-weight-semibold)] tw-font-semibold">Preview Table</div>
                        <div className="!tw-text-[var(--secondary-color)] !tw-text-[12px] tw-text-slate-500 tw-text-sm">
                          Review a row, edit any field, then verify before importing.
                        </div>
                      </div>
                      <span aria-live="polite" className={`tw-rounded-full tw-px-3 tw-py-1 tw-text-xs tw-font-semibold ${bulkUploadLoading && bulkCheckMode ? 'tw-bg-blue-100 tw-text-blue-700' : bulkIsVerified ? 'tw-bg-emerald-100 tw-text-emerald-700' : bulkUploadSummary.invalidCount > 0 ? 'tw-bg-rose-100 tw-text-rose-700' : 'tw-bg-amber-100 tw-text-amber-800'}`}>
                        {bulkUploadLoading && bulkCheckMode ? 'Checking data…' : bulkIsVerified ? 'Verified · Ready to import' : bulkUploadSummary.invalidCount > 0 ? `${bulkUploadSummary.invalidCount} row(s) need fixes` : 'Verify to enable import'}
                      </span>
                    </div>
                  </div>
                  <div className="tw-min-h-0 tw-flex-1 tw-overflow-y-auto tw-p-4">
                    {bulkUploadLoading && bulkCheckMode ? (
                      <div className="tw-flex tw-h-full tw-min-h-[280px] tw-flex-col tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-blue-100 tw-bg-blue-50 tw-p-6 tw-text-center" role="status">
                        <span className="tw-mb-4 tw-h-10 tw-w-10 tw-animate-spin tw-rounded-full tw-border-4 tw-border-blue-200 tw-border-t-blue-600" aria-hidden="true" />
                        <h6 className="tw-mb-1 tw-text-lg tw-font-semibold tw-text-slate-900">{bulkCheckMode === 'initial' ? 'Checking uploaded data' : 'Verifying your changes'}</h6>
                        <p className="tw-mb-0 tw-text-sm tw-text-slate-600">Checking fields, duplicate emails, and existing trainees. Please wait…</p>
                      </div>
                    ) : bulkSelectedRow ? (
                      <>
                        <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
                          <div>
                            <div className="tw-font-semibold tw-text-slate-900">Row {bulkSelectedRow.rowNumber}: {bulkSelectedRow.values.name || 'Unnamed trainee'}</div>
                            <div className="tw-text-xs tw-text-slate-500">All {BULK_TRAINEE_COLUMNS.length} fields are available below. Changes are kept here until import.</div>
                          </div>
                          <span className={`tw-rounded-full tw-px-3 tw-py-1 tw-text-xs tw-font-semibold ${bulkSelectedRow.hasIssues ? 'tw-bg-rose-100 tw-text-rose-700' : bulkHasCurrentServerCheck ? bulkSelectedRow.exists ? 'tw-bg-blue-100 tw-text-blue-700' : 'tw-bg-emerald-100 tw-text-emerald-700' : 'tw-bg-amber-100 tw-text-amber-800'}`}>
                            {bulkSelectedRow.hasIssues ? 'Needs fixes' : bulkHasCurrentServerCheck ? bulkSelectedRow.exists ? 'Existing trainee · Update' : 'New trainee · Create' : 'Needs verification'}
                          </span>
                        </div>
                        <div className="tw-grid tw-gap-x-3 tw-gap-y-2 sm:tw-grid-cols-2 lg:tw-grid-cols-3 xl:tw-grid-cols-4">
                          {BULK_TRAINEE_COLUMNS.map((column) => {
                            const fieldIssues = bulkSelectedRow.issueMap?.[column.key] || [];
                            return (
                              <label key={column.key} className="tw-min-w-0 tw-text-sm">
                                <span className="tw-mb-1 tw-block tw-font-medium tw-text-slate-700">{column.label}{column.key === 'email' && <span className="tw-ml-1 tw-text-xs tw-text-blue-600">Matching key</span>}</span>
                                {renderBulkPreviewCell(bulkSelectedRow, column)}
                                {fieldIssues.length > 0 && <span className="tw-mt-1 tw-block tw-text-xs tw-text-rose-700">{fieldIssues.join(', ')}</span>}
                              </label>
                            );
                          })}
                        </div>
                        {bulkSelectedRow.issueMap?.row?.length > 0 && (
                          <div className="tw-mt-3 tw-rounded-lg tw-bg-rose-50 tw-p-3 tw-text-sm tw-text-rose-800">{bulkSelectedRow.issueMap.row.join(', ')}</div>
                        )}
                        <p className="tw-mb-0 tw-mt-3 tw-text-xs tw-text-slate-500">Matching emails update existing accounts, including passwords. Changing an email can change whether a row is new or existing.</p>
                      </>
                    ) : (
                      <div className="tw-rounded-xl tw-border tw-border-slate-200 tw-bg-slate-50 tw-p-6 tw-text-center tw-text-sm tw-text-slate-600">
                        {bulkUploadRows.length === 0 ? 'No preview rows available.' : 'No rows match this filter.'}
                      </div>
                    )}
                  </div>
                </section>
              </div>
            </div>
          )}

          {bulkUploadStep === 'importing' && (
            <div className="tw-text-center tw-py-12 tw-px-6">
              <div className="!tw-text-[var(--primary-color)] tw-w-[1.5em] tw-h-[1.5em] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] ui-spinner tw-text-brand-600 tw-mb-4" role="status">
                <span className="tw-sr-only">Loading...</span>
              </div>
              <h5 className="tw-mb-2">Importing trainees...</h5>
              <p className="!tw-text-[var(--secondary-color)] tw-text-slate-500 tw-mb-0">Please wait while we validate and import the previewed rows.</p>
            </div>
          )}

          {bulkUploadStep === 'result' && (
            <div className="tw-p-5 sm:tw-p-6">
              <div className="tw-rounded-2xl tw-border tw-border-emerald-100 tw-bg-emerald-50/40 tw-p-6 tw-text-center">
                <div className="tw-mx-auto tw-mb-3 tw-flex tw-h-14 tw-w-14 tw-items-center tw-justify-center tw-rounded-full tw-bg-emerald-100">
                  <Icon icon="fluent:checkmark-24-filled" width="30" height="30" className="tw-text-emerald-700" />
                </div>
                <h4 className="tw-mb-1 tw-text-xl tw-font-semibold">Import complete</h4>
                <p className="tw-mb-5 tw-text-sm tw-text-slate-600">{(bulkUploadResult?.importedCount ?? 0) + (bulkUploadResult?.updatedCount ?? 0)} trainee(s) processed successfully.</p>
                <div className="tw-grid tw-gap-3 sm:tw-grid-cols-2" aria-live="polite">
                  <div className="tw-rounded-xl tw-border tw-border-emerald-200 tw-bg-white tw-p-4 tw-text-left">
                    <div className="tw-text-sm tw-text-slate-600">New trainees created</div>
                    <div className="tw-text-3xl tw-font-semibold tw-text-emerald-700">{bulkUploadResult?.importedCount ?? 0}</div>
                  </div>
                  <div className="tw-rounded-xl tw-border tw-border-blue-200 tw-bg-white tw-p-4 tw-text-left">
                    <div className="tw-text-sm tw-text-slate-600">Existing trainees updated</div>
                    <div className="tw-text-3xl tw-font-semibold tw-text-blue-700">{bulkUploadResult?.updatedCount ?? 0}</div>
                  </div>
                </div>
                <p className="tw-mb-0 tw-mt-4 tw-text-xs tw-text-slate-600">Existing trainees were matched by email. Their email addresses were not changed.</p>
              </div>
            </div>
          )}
        </Dialog.Body>
        <Dialog.Footer className="tw-border-t">
          <button
            type="button"
            className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-p-[8px_16px] tw-text-[0.875rem] tw-font-medium tw-rounded-[0.5rem] [transition:all_0.2s_ease] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[1px] tw-pl-4 tw-pr-4 tw-pt-2.5 tw-pb-2.5 tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [--tw-border-opacity:1] tw-border-[rgb(226_232_240_/_var(--tw-border-opacity,_1))] [--tw-bg-opacity:1] tw-bg-[rgb(255_255_255_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(51_65_85_/_var(--tw-text-opacity,_1))] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(248_250_252_/_var(--tw-bg-opacity,_1))] ui-button ui-button-light"}
            onClick={handleCloseBulkUploadModal}
            disabled={bulkUploadLoading}
            style={{ marginRight: bulkUploadStep === 'result' ? 0 : 'auto' }}
          >
            {bulkUploadStep === 'result' ? 'Close' : 'Cancel'}
          </button>
          {bulkUploadStep === 'preview' && (
            <button
              type="button"
              className="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border tw-border-blue-600 tw-bg-white tw-px-4 tw-py-2.5 tw-font-medium tw-text-blue-700 hover:tw-bg-blue-50 disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
              onClick={handleBulkVerify}
              disabled={bulkUploadLoading || bulkUploadRows.length === 0}
            >
              {bulkCheckMode === 'verify'
                ? <span className="tw-h-4 tw-w-4 tw-animate-spin tw-rounded-full tw-border-2 tw-border-blue-200 tw-border-t-blue-700" aria-hidden="true" />
                : <Icon icon="fluent:checkmark-circle-24-regular" width="18" height="18" />}
              {bulkCheckMode === 'verify' ? 'Verifying…' : bulkIsVerified ? 'Verify again' : 'Verify changes'}
            </button>
          )}
          {(bulkUploadStep === 'preview' || bulkUploadStep === 'importing') && (
            <button
              className={"[&.loading]:tw-bg-[#e9ecef] [&.loading]:tw-border-[#e9ecef] [&.loading]:tw-text-[#6c757d] [&.loading]:tw-cursor-not-allowed [&.loading]:tw-opacity-[0.8] [&.loading:hover]:tw-bg-[#e9ecef] [&.loading:hover]:tw-border-[#e9ecef] [&.loading:hover]:[transform:none] max-[768px]:tw-p-[5px_10px] max-[768px]:tw-text-[13px] [&:disabled:hover]:!tw-bg-[#e9ecef] [&:disabled:hover]:!tw-text-[#6c757d] [&:disabled:hover]:!tw-cursor-not-allowed [&:disabled:hover]:![transform:none] [&:disabled:hover]:!tw-opacity-[0.65] [&:disabled:hover]:!tw-shadow-[none] [&:disabled:hover]:![pointer-events:all] [&[disabled]]:!tw-bg-[#e9ecef] [&[disabled]]:!tw-text-[#6c757d] [&[disabled]]:!tw-cursor-not-allowed [&[disabled]]:![transform:none] [&[disabled]]:!tw-opacity-[0.65] [&[disabled]]:!tw-shadow-[none] [&[disabled]]:![pointer-events:all] [&[disabled]:hover]:!tw-bg-[#e9ecef] [&[disabled]:hover]:!tw-text-[#6c757d] [&[disabled]:hover]:!tw-cursor-not-allowed [&[disabled]:hover]:![transform:none] [&[disabled]:hover]:!tw-opacity-[0.65] [&[disabled]:hover]:!tw-shadow-[none] [&[disabled]:hover]:![pointer-events:all] [[data-theme='dark']_&:disabled]:!tw-bg-[#2D3748] [[data-theme='dark']_&:disabled]:!tw-text-[#718096] [[data-theme='dark']_&.loading:disabled]:!tw-bg-[var(--primary-color)] [[data-theme='dark']_&.loading:disabled]:!tw-text-[white] tw-inline-flex tw-items-center tw-text-[0.875rem] tw-rounded-[0.5rem] tw-shrink-[0] tw-justify-center tw-gap-2 tw-border-[transparent] tw-leading-[1.25rem] [text-decoration-line:none] [transition-property:color,_background-color,_border-color,_text-decoration-color,_fill,_stroke] [transition-timing-function:cubic-bezier(0.4,_0,_0.2,_1)] [transition-duration:150ms] [&:focus-visible]:[outline:2px_solid_transparent] [&:focus-visible]:[outline-offset:2px] [&:focus-visible]:[--tw-ring-offset-shadow:var(--tw-ring-inset)_0_0_0_var(--tw-ring-offset-width)_var(--tw-ring-offset-color)] [&:focus-visible]:[--tw-ring-shadow:var(--tw-ring-inset)_0_0_0_calc(2px_+_var(--tw-ring-offset-width))_var(--tw-ring-color)] [&:focus-visible]:tw-shadow-[var(--tw-ring-offset-shadow),_var(--tw-ring-shadow),_var(--tw-shadow,_0_0_#0000)] [&:focus-visible]:[--tw-ring-opacity:1] [&:focus-visible]:[--tw-ring-color:rgb(82_101_232_/_var(--tw-ring-opacity,_1))] [&:focus-visible]:[--tw-ring-offset-width:2px] [&:disabled]:!tw-bg-[#e9ecef] [&:disabled]:!tw-text-[#6c757d] [&:disabled]:!tw-cursor-not-allowed [&:disabled]:![transform:none] [&:disabled]:!tw-opacity-[0.65] [&:disabled]:!tw-shadow-[none] [&:disabled]:![pointer-events:all] [&.ui-button-sm]:tw-p-[6px_16px] [&.ui-button-sm]:tw-text-[14px] [&.ui-button-sm]:tw-rounded-[4px] tw-border-0 tw-p-[0.8rem] tw-font-medium [transition:all_0.3s_ease] [--tw-bg-opacity:1] tw-bg-[rgb(64_84_214_/_var(--tw-bg-opacity,_1))] [--tw-text-opacity:1] tw-text-[rgb(255_255_255_/_var(--tw-text-opacity,_1))] [--tw-shadow:0_1px_2px_0_rgb(0_0_0_/_0.05)] [--tw-shadow-colored:0_1px_2px_0_var(--tw-shadow-color)] tw-shadow-[var(--tw-ring-offset-shadow,_0_0_#0000),_var(--tw-ring-shadow,_0_0_#0000),_var(--tw-shadow)] [&:hover]:[transform:translateY(-2px)] [&:hover]:[--tw-bg-opacity:1] [&:hover]:tw-bg-[rgb(53_71_186_/_var(--tw-bg-opacity,_1))] ui-button ui-button-primary"}
              onClick={handleBulkUploadSubmit}
              disabled={bulkIsImportDisabled}
            >
              {bulkUploadStep === 'importing' ? (
                <>
                  <span className="tw-text-[#3152e8] tw-inline-block tw-shrink-[0] tw-animate-[tw-spin_1s_linear_infinite] tw-rounded-full tw-border-[2px] tw-border-[currentColor] tw-border-r-[transparent] tw-w-[1em] tw-h-[1em] ui-spinner ui-spinner-sm tw-me-2" aria-hidden="true" />
                  Importing...
                </>
              ) : (
                <>
                  <Icon icon="fluent:arrow-upload-24-regular" width="18" height="18" className="tw-me-2" />
                  Import Trainees
                </>
              )}
            </button>
          )}
        </Dialog.Footer>
      </Dialog>

    </>
  );
}

export default Trainees;
